import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
    Send, Globe, FileText, Paperclip, Plus, Square,
    MessageSquare, Trash2, ChevronLeft, Brain, Zap, Leaf, ChevronDown,
    Clock, Lightbulb, Pin, Wand2, Upload, X, Check, ArrowRight,
    CheckSquare, Menu, PanelLeft, Pencil, SquareCheck, WifiOff, FileText as FileIcon,
    ListChecks, MonitorPlay, GitBranch
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '../../components/ui/sheet';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useProjectContext } from '../../context/ProjectContext';
import {
    useChatSessions, useModels, useChatSession,
    useCreateChatSession, useDeleteChatSession, useUpdateChatSession, useSendChatMessage,
    useProjectTasks, useExecuteToolCall, useUpdateFile, useEditDocument, useAcceptDocumentChanges,
    useCreateFile,
    useCreateTask, useUpdateTask, useDeleteTask,
    useTaskChangesets, useRevertTaskChange, useRevertTaskBatch, useSeedTaskChangesets,
    useTruncateChatMessages, useBranchChatSession
} from '../../hooks/useProjectQueries';
import { AgentEditorPanel } from '../../components/chat/AgentEditorPanel';
import { MockupPreviewPanel } from '../../components/chat/MockupPreviewPanel';
import { TaskAgentPanel, TASK_PANEL_MIN_WIDTH, TASK_PANEL_MAX_WIDTH } from '../../components/chat/TaskAgentPanel';
import { ArtifactPanelSwitcher } from '../../components/chat/ArtifactPanelSwitcher';
import { collectTaskOutcomeNotes, mergeTaskOutcomeNotes } from '../../components/chat/taskOutcomes';
import { buildNewTask, withResolvedDueDate } from '../../components/tasks/buildTask';
import { Surface } from '../../components/layout/Surface';
import { ArtifactSurface } from '../../components/layout/ArtifactSurface';
import { useIsMobile } from '../../hooks/useIsMobile';
import { RichInput } from '../../components/chat/RichInput';
import { ChatMessage } from '../../components/chat/ChatMessage';
import { ChatEmptyState } from '../../components/chat/ChatEmptyState';
import { AgentWorkingState } from '../../components/chat/AgentWorkingState';
import { ScrollToLatest } from '../../components/chat/ScrollToLatest';
import { ChatPlaceholderOverlay } from '../../components/chat/ChatPlaceholderOverlay';
import { ChatTranscriptSkeleton } from '../../components/chat/ChatTranscriptSkeleton';
import { ChatErrorTurn } from '../../components/chat/ChatErrorTurn';
import { useShowcasePlayback } from '../../components/chat/showcase/useShowcasePlayback';
import {
    ShowcaseDemoBar, ShowcaseIntroCard, ShowcaseOutroCard
} from '../../components/chat/showcase/ShowcaseFrame';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

import { cn, isAbortError } from '../../lib/utils';
import { describeError } from '../../lib/errors';

// Tool names that mutate tasks. `modify_task` is the legacy single-task shape:
// never declared to the model and never emitted, kept only so conversations
// persisted before batching still render their chips and stay revertable.
const TASK_TOOLS = ['create_tasks', 'update_tasks', 'delete_tasks', 'modify_task'];

/**
 * Every tool name this transcript knows how to act on — task tools included,
 * even though the server applies those, because "handled" here means "will not
 * silently vanish". Anything outside this set gets an error rather than a
 * skeleton that quietly flips to done.
 */
const DOCUMENT_TOOLS = [
    'create_document', 'rewrite_document', 'insert_in_document', 'replace_in_document',
    'create_mockup', 'rewrite_mockup', 'insert_in_mockup', 'replace_in_mockup',
];

const KNOWN_TOOLS = new Set([...TASK_TOOLS, ...DOCUMENT_TOOLS]);

/**
 * Tools that claim the right-hand surface for themselves.
 *
 * A document or mockup edit arrives as a diff with an accept/reject decision
 * attached, and it needs the width to be read at all. A changeset needs
 * neither. So on a turn that produced both, the document wins and the task
 * panel stays one click away in the switcher — opening it only to have the
 * document replace it a moment later is worse than never opening it.
 */
const SURFACE_CLAIMING_TOOLS = new Set(DOCUMENT_TOOLS);

/**
 * Drop trailing `stopped` / `error` markers.
 *
 * Those markers are local-only — the server never stores them — so every one
 * left in the array shifts our indices out of step with the persisted ones that
 * `message_index` and the truncate endpoint are addressed by. Keeping them
 * strictly tail-anchored (stripped the moment another turn is sent) is what
 * keeps the two arrays aligned everywhere it matters.
 */
function trimTrailingMarkers(msgs) {
    let end = msgs.length;
    while (end > 0 && (msgs[end - 1]?.stopped || msgs[end - 1]?.error)) end--;
    return end === msgs.length ? msgs : msgs.slice(0, end);
}

/** Short label for the model behind each preset, for the turn meta line. */
const MODEL_LABELS = {
    fast: 'flash',
    efficient: 'flash',
    powerful: 'pro',
};

/**
 * The thing a tool call acts on, for the skeleton card's title.
 * Returns null when the arguments carry no name worth showing — the card then
 * falls back to a generic label rather than inventing one.
 */
function describeToolTarget(toolCall) {
    const args = toolCall?.arguments || {};
    if (args.name) return args.name;
    if (args.file_name) return args.file_name;
    if (Array.isArray(args.tasks)) {
        const n = args.tasks.length;
        return `${n} ${n === 1 ? 'task' : 'tasks'}`;
    }
    if (Array.isArray(args.updates)) {
        const n = args.updates.length;
        return `${n} ${n === 1 ? 'task' : 'tasks'}`;
    }
    if (args.task_title) return args.task_title;
    return null;
}

/**
 * Decide whether an agent edit is still pending, already accepted, or stale
 * because the file moved on underneath it.
 *
 * Hoisted to module scope and consumed through a memo: it used to be redefined
 * and re-run inside the message map on every render, costing two `files.find()`
 * per message and handing every message a fresh object identity.
 */
function computeEditStatus(editItem, files) {
    if (!editItem) return null;
    let isStale = false;
    let isAccepted = false;

    // Try to find by ID first, then by name (crucial for history items)
    const currentFile = files.find(f => f.id === editItem.file.id) ||
        files.find(f => f.name === editItem.file.name);

    const modifiedContent = editItem.diffData?.modified_content || editItem.modifiedContent;
    const originalContentForCheck = editItem.diffData?.original_content || editItem.originalContent;

    if (currentFile && modifiedContent) {
        const normalize = (str) => (str || '').replace(/\r\n/g, '\n').trim();
        const current = normalize(currentFile.content);
        const modified = normalize(modifiedContent);
        const original = normalize(originalContentForCheck);

        if (current === modified) isAccepted = true;
        else if (current !== original) isStale = true;
    }
    return { ...editItem, isStale, isAccepted };
}

/** Map every entry of an edits-by-message-index map through computeEditStatus. */
function withEditStatus(map, files) {
    const out = {};
    for (const key of Object.keys(map || {})) {
        out[key] = computeEditStatus(map[key], files);
    }
    return out;
}

/**
 * Every persisted `tool` row belonging to the turn at `index`.
 *
 * A turn can produce more than one — a prompt that rewrites a document *and*
 * updates tasks persists a row per tool — so reading only `messages[index + 1]`
 * silently hands a tool call the wrong payload.
 */
function collectToolRows(messages, index) {
    const rows = [];
    for (let j = index + 1; j < messages.length && messages[j]?.role === 'tool'; j++) {
        const raw = messages[j].content;
        try {
            rows.push(typeof raw === 'string' ? JSON.parse(raw) : raw);
        } catch (e) {
            console.error('Failed to parse tool result during rehydration:', e);
        }
    }
    return rows;
}

/**
 * The row a given tool call produced, removed from `rows` as it is claimed — so
 * two calls to the same tool in one turn each get their own row, in order.
 *
 * The positional fallback covers rows persisted before `tool_name` was written.
 */
function takeToolRow(rows, toolName) {
    let i = rows.findIndex(r => r?.tool_name === toolName);
    if (i === -1) i = rows.findIndex(r => r && r.tool_name === undefined);
    return i === -1 ? null : rows.splice(i, 1)[0];
}

const FILE_TAG_RE = /@\[File:\s*([^\]]+)\]/g;
const QUOTED_FILE_TAG_RE = /\[Referenced\s+([^\]:\n]+)(?::L?\d+-\d+)?\]/g;
const TASK_TAG_RE = /@\[Task:\s*([^\]]+)\]/g;
const LINE_SUFFIX_RE = /:L?\d+-\d+$/;

/**
 * Collect all files and tasks referenced or touched across the conversation
 * up to and including the message being sent.
 */
function collectConversationReferences(historyMessages, currentText, explicitItems, allFiles, allTasks) {
    const fileIdSet = new Set();
    const fileNameSet = new Set();
    const taskIdSet = new Set();
    const taskTitleSet = new Set();

    const addFileById = (fid) => {
        if (!fid || fid === 'history') return;
        const found = allFiles.find(f => f.id === fid);
        if (found) {
            fileIdSet.add(found.id);
            fileNameSet.add(found.name);
        } else {
            fileIdSet.add(fid);
        }
    };

    const addFileByName = (rawName) => {
        if (!rawName) return;
        const cleaned = rawName.trim().replace(LINE_SUFFIX_RE, '').trim();
        if (!cleaned) return;
        const found = allFiles.find(f => f.name === cleaned);
        if (found) {
            fileIdSet.add(found.id);
            fileNameSet.add(found.name);
        } else {
            fileNameSet.add(cleaned);
        }
    };

    const addTaskById = (tid) => {
        if (!tid) return;
        const found = allTasks.find(t => t.id === tid);
        if (found) {
            taskIdSet.add(found.id);
            taskTitleSet.add(found.title);
        } else {
            taskIdSet.add(tid);
        }
    };

    const addTaskByTitle = (rawTitle) => {
        if (!rawTitle) return;
        const cleaned = rawTitle.trim();
        if (!cleaned) return;
        const found = allTasks.find(t => t.title === cleaned);
        if (found) {
            taskIdSet.add(found.id);
            taskTitleSet.add(found.title);
        } else {
            taskTitleSet.add(cleaned);
        }
    };

    const scanText = (text) => {
        if (!text || typeof text !== 'string') return;
        for (const match of text.matchAll(FILE_TAG_RE)) addFileByName(match[1]);
        for (const match of text.matchAll(QUOTED_FILE_TAG_RE)) addFileByName(match[1]);
        for (const match of text.matchAll(TASK_TAG_RE)) addTaskByTitle(match[1]);
    };

    for (const msg of historyMessages || []) {
        if (!msg) continue;
        if (msg.role === 'user') {
            (msg.referenced_files || []).forEach(addFileById);
            (msg.referenced_tasks || []).forEach(addTaskById);
            scanText(msg.content);
        } else if (msg.role === 'model') {
            for (const tc of msg.tool_calls || []) {
                const args = tc?.arguments || {};
                if (DOCUMENT_TOOLS.includes(tc?.tool_name) || tc?.tool_name === 'modify_document') {
                    addFileById(args.file_id);
                    addFileByName(args.file_name || args.name);
                } else if (TASK_TOOLS.includes(tc?.tool_name)) {
                    addTaskById(args.task_id);
                    addTaskByTitle(args.task_title);
                    for (const item of [...(args.updates || []), ...(args.tasks || [])]) {
                        addTaskById(item?.task_id);
                        addTaskByTitle(item?.task_title || item?.title);
                    }
                }
            }
        } else if (msg.role === 'tool') {
            let parsed = msg.content;
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch { parsed = null; }
            }
            const res = parsed?.result;
            if (res) {
                addFileById(res.file_id);
                addFileByName(res.file_name || res.name);
            }
        }
    }

    for (const item of explicitItems || []) {
        if (item.type === 'file') {
            addFileById(item.id);
            addFileByName(item.name);
        } else if (item.type === 'task') {
            addTaskById(item.id);
            addTaskByTitle(item.title);
        }
    }

    scanText(currentText);

    return {
        fileIds: Array.from(fileIdSet),
        fileNames: Array.from(fileNameSet),
        taskIds: Array.from(taskIdSet),
        taskTitles: Array.from(taskTitleSet),
    };
}


export default function ProjectChat() {
    const { projectId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    // Carries the "Send to Advisor" handover from the Artifacts route.
    const location = useLocation();

    // Get files from context
    const { files: contextFiles, isLoadingFiles, readOnly, baseUrl } = useProjectContext();
    const files = contextFiles || [];

    // ═══════════════════════════════════════════════════════════════════════
    // SHARED-LINK SHOWCASE
    //
    // Visitors on /s/:token get a scripted demo instead of a transcript. It is
    // loaded on demand: the script carries two full mockup revisions and two
    // document revisions, and shipping ~25 KB of promo copy to every signed-in
    // user who will never see it is pure dead weight in the main chunk.
    // ═══════════════════════════════════════════════════════════════════════
    const [showcase, setShowcase] = useState(null);

    useEffect(() => {
        if (!readOnly) return undefined;
        let alive = true;
        import('../../components/chat/showcase/showcaseConversation')
            .then(mod => { if (alive) setShowcase(mod); })
            .catch(err => console.error('Failed to load the showcase conversation:', err));
        return () => { alive = false; };
    }, [readOnly]);

    // Get tasks. The demo needs real-shaped tasks of its own: `projectId` is
    // undefined on a shared route (the URL carries a token, not an id), so this
    // query never resolves there and the task panel would open empty.
    const tasksQuery = useProjectTasks(projectId);
    const tasks = readOnly ? (showcase?.SHOWCASE_TASKS || []) : (tasksQuery.data || []);

    // React Query hooks for sessions and models
    const sessionsQuery = useChatSessions(projectId);
    const modelsQuery = useModels();
    const createSessionMutation = useCreateChatSession(projectId);
    const deleteSessionMutation = useDeleteChatSession(projectId);
    const updateSessionMutation = useUpdateChatSession(projectId);
    const sendMessageMutation = useSendChatMessage(projectId);
    const truncateMessagesMutation = useTruncateChatMessages(projectId);
    const branchSessionMutation = useBranchChatSession(projectId);
    const executeToolMutation = useExecuteToolCall(projectId);
    const updateFileMutation = useUpdateFile(projectId);
    const editDocumentMutation = useEditDocument(projectId);
    const acceptChangesMutation = useAcceptDocumentChanges(projectId);
    const createFileMutation = useCreateFile(projectId);

    const sessions = sessionsQuery.data || [];
    const models = modelsQuery.data || {};

    // Session state
    const [currentSessionId, setCurrentSessionId] = useState(() => {
        // Initialize from URL if present
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('session') || null;
    });
    const [messages, setMessages] = useState([]);
    const [showSidebar, setShowSidebar] = useState(true);
    // Starts true: until the sessions query resolves we do not know whether this
    // chat has history, and guessing "empty" flashes the hero over a
    // conversation that is about to arrive. `createNewSession` clears it
    // immediately, since a new chat is genuinely empty from the first frame.
    const [sessionLoading, setSessionLoading] = useState(true);
    // Set when a session fails to load, so the transcript can offer a real
    // retry instead of leaving an empty column behind a dismissed toast.
    const [sessionError, setSessionError] = useState(null);

    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);

    // ─── Turn telemetry ──────────────────────────────────────────────────────
    // Only ever holds facts about turns produced in THIS session. Rehydrated
    // history has no model or timing recorded, and the meta line stays bare
    // rather than showing an invented number.
    const [turnStartedAt, setTurnStartedAt] = useState(null);
    const [activeTurnContext, setActiveTurnContext] = useState(null);
    const [announcedTools, setAnnouncedTools] = useState(null);
    const [turnMetaByIndex, setTurnMetaByIndex] = useState({});
    const [pendingToolsByIndex, setPendingToolsByIndex] = useState({});

    // ─── Scroll + composer chrome ────────────────────────────────────────────
    const [showJumpToLatest, setShowJumpToLatest] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const reduceMotion = useReducedMotion();
    // Declared up here because effects and derived layout values below read it.
    const isMobile = useIsMobile();
    const isOnline = useOnlineStatus();

    // Rename chat session state
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [editingSessionSource, setEditingSessionSource] = useState(null); // 'sidebar' | 'header' | null

    // In-place user message editing state
    const [editingMessageIndex, setEditingMessageIndex] = useState(null);
    const [editingMessageText, setEditingMessageText] = useState('');
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

    // Draft inputs per session (and 'new' session)
    const [draftsMap, setDraftsMap] = useState({});
    const sidebarWasOpenRef = useRef(true);

    // Settings
    const [isFullContext, setIsFullContext] = useState(false);
    const [isWebSearch, setIsWebSearch] = useState(false);
    const [modelPreset, setModelPreset] = useState('fast');
    const [showModelPicker, setShowModelPicker] = useState(false);

    // Reference Logic
    const [referencedItems, setReferencedItems] = useState([]); // Array of { type: 'file' | 'task', id, name/title }
    const [attachedSelections, setAttachedSelections] = useState([]); // Array of { id, file, text, range }
    const [showMentionPicker, setShowMentionPicker] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);

    // Delete confirmation
    const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);

    // ═══════════════════════════════════════════════════════════════════════
    // ARTIFACT PANEL — which one owns the right-hand surface
    //
    // One value, not three booleans. Stacking panels squeezes the conversation
    // to nothing on anything narrower than an ultrawide, so the rule was always
    // "one at a time" — but it used to be enforced by an effect that closed the
    // task panel whenever either of the others opened. That effect raced the
    // tool loop it was supposed to arbitrate: a turn that wrote a document AND
    // touched tasks applied the tasks invisibly, because the document's
    // `setEditorPanelOpen(true)` landed in a later commit and shut the task
    // panel back down. Making the rule structural means it cannot be lost to
    // ordering, and the panel that ends up open is now a decision taken once,
    // at the end of the turn, rather than whichever setter fired last.
    // ═══════════════════════════════════════════════════════════════════════
    const [activePanel, setActivePanel] = useState(null); // null | 'editor' | 'mockup' | 'tasks'

    // Derived during render, so every `xPanelOpen` read below is unchanged and
    // there is no second copy of this state that can drift out of step.
    const editorPanelOpen = activePanel === 'editor';
    const mockupPanelOpen = activePanel === 'mockup';
    const taskPanelOpen = activePanel === 'tasks';

    // Close — but only if that panel is the one actually showing. A close
    // handler that fires late (the Monaco unmount dance below schedules one on
    // a timer) must not shut whatever the user opened in the meantime.
    const closePanel = useCallback((id) => {
        setActivePanel(prev => (prev === id ? null : prev));
    }, []);

    // Agentic AI state - Editor panel for created/modified documents
    const [agentFile, setAgentFile] = useState(null); // Currently open file in editor panel
    const [isSavingFile, setIsSavingFile] = useState(false);
    const [createdFilesMap, setCreatedFilesMap] = useState({}); // Map<messageIndex, fileInfo> - persists cards even when panel closed

    // Document editing state - For diff view
    const [editedFilesMap, setEditedFilesMap] = useState({}); // Map<messageIndex, { file, originalContent, editType, editSummary }>
    const [isDiffMode, setIsDiffMode] = useState(false);
    const [originalContent, setOriginalContent] = useState('');
    const [editSummary, setEditSummary] = useState('');
    const [isDiffAccepted, setIsDiffAccepted] = useState(false); // Track if viewing an already-accepted diff

    // Mockup state - For mockup preview panel
    const [createdMockupsMap, setCreatedMockupsMap] = useState({}); // Map<messageIndex, mockupInfo>
    const [editedMockupsMap, setEditedMockupsMap] = useState({}); // Map<messageIndex, { file, originalContent, editType, editSummary }>
    const [mockupPanelFile, setMockupPanelFile] = useState(null); // Currently viewing mockup
    const [isMockupDiffMode, setIsMockupDiffMode] = useState(false);
    const [mockupOriginalContent, setMockupOriginalContent] = useState('');
    const [mockupEditSummary, setMockupEditSummary] = useState('');
    const [isMockupDiffAccepted, setIsMockupDiffAccepted] = useState(false);

    // ═══════════════════════════════════════════════════════════════════════
    // TASK PANEL - the agent's task changeset + a live editable board
    // ═══════════════════════════════════════════════════════════════════════
    const [taskPanelTab, setTaskPanelTab] = useState('changes');
    const [focusBatchId, setFocusBatchId] = useState(null);
    const [revertingIds, setRevertingIds] = useState(() => new Set());

    // Task mutations are session-scoped so the user's own panel edits land in
    // this conversation's change log — that's how the agent learns about them.
    const createTaskMutation = useCreateTask(projectId, currentSessionId);
    const updateTaskMutation = useUpdateTask(projectId, currentSessionId);
    const deleteTaskMutation = useDeleteTask(projectId, currentSessionId);
    const revertChangeMutation = useRevertTaskChange(projectId, currentSessionId);
    const revertBatchMutation = useRevertTaskBatch(projectId, currentSessionId);
    const seedTaskChangesets = useSeedTaskChangesets(projectId, currentSessionId);

    // Only fetched for conversations that actually involve tasks — no reason to
    // pay for it in a chat that never touches them.
    //
    // Derived during render rather than tracked in state: as an effect-set flag
    // this was clobbered by the session-reset effect below, which is declared
    // after the detector and ran in the same commit whenever `loadSession` set
    // the session id and the messages together. The query then stayed disabled
    // for the whole visit and every past turn silently lost its chip. A memo in
    // the render body has no ordering against an effect, so it cannot lose.
    const transcriptTouchedTasks = useMemo(
        () => messages.some(
            msg => msg.role === 'model' && msg.tool_calls?.some(tc => TASK_TOOLS.includes(tc.tool_name))
        ),
        [messages]
    );
    const taskChangesetsQuery = useTaskChangesets(projectId, currentSessionId, {
        enabled: !readOnly && (transcriptTouchedTasks || taskPanelOpen),
    });
    const taskBatches = readOnly
        ? (showcase?.SHOWCASE_TASK_BATCHES || [])
        : (taskChangesetsQuery.data || []);

    // messageIndex -> that turn's changeset, for the in-message summary chip.
    const batchesByMessageIndex = useMemo(() => {
        const map = new Map();
        for (const batch of taskBatches) {
            if (batch.source !== 'ai' || batch.message_index === null || batch.message_index === undefined) continue;
            const existing = map.get(batch.message_index);
            if (existing) {
                // A turn can fire several task tools; merge them into one chip.
                existing.summary.created += batch.summary.created;
                existing.summary.updated += batch.summary.updated;
                existing.summary.deleted += batch.summary.deleted;
                existing.total += batch.total;
                existing.active += batch.active;
                existing.conflicted = existing.conflicted || batch.conflicted;
            } else {
                map.set(batch.message_index, {
                    batch_id: batch.batch_id,
                    summary: { ...batch.summary },
                    total: batch.total,
                    active: batch.active,
                    conflicted: batch.conflicted,
                });
            }
        }
        return map;
    }, [taskBatches]);

    // Turn index -> the batch ids that turn produced, read straight out of the
    // transcript. This is what makes the chip survive a reload: `message_index`
    // above is a client-computed array position stored server-side, so a retry
    // or a failed send can leave it pointing at the wrong slot — while a `tool`
    // row travels with the reply it belongs to and is trimmed alongside it.
    const taskBatchRefsByIndex = useMemo(() => {
        const map = new Map();
        messages.forEach((msg, index) => {
            if (msg.role !== 'model' || !msg.tool_calls?.length) return;

            // `task_batches` is written onto the reply by the server, in the same
            // update that appends the message, so it is always in step with the
            // turn it belongs to. The `tool` rows below are the older shape —
            // written by the browser after the fact, and kept only so
            // conversations recorded that way still show their chips.
            const refs = (msg.task_batches?.length
                ? msg.task_batches
                : collectToolRows(messages, index).map(row => row?.task_batch)
            ).filter(ref => ref?.batch_id);
            if (!refs.length) return;
            // A turn can fire several task tools; they merge into one chip.
            // `noop` and `skipped` are counted here and nowhere else: they are
            // the outcomes that wrote nothing, so the change log has no row for
            // them and the transcript is the only place they survive.
            map.set(index, refs.reduce((acc, ref) => ({
                batch_ids: [...acc.batch_ids, ref.batch_id],
                total: acc.total + (ref.total || 0),
                noop: acc.noop + (ref.noop?.length || 0),
                skipped: acc.skipped + (ref.skipped?.length || 0),
                summary: {
                    created: acc.summary.created + (ref.summary?.created || 0),
                    updated: acc.summary.updated + (ref.summary?.updated || 0),
                    deleted: acc.summary.deleted + (ref.summary?.deleted || 0),
                },
            }), { batch_ids: [], total: 0, noop: 0, skipped: 0, summary: { created: 0, updated: 0, deleted: 0 } }));
        });
        return map;
    }, [messages]);

    const liveBatchById = useMemo(
        () => new Map(taskBatches.filter(b => b.batch_id).map(b => [b.batch_id, b])),
        [taskBatches]
    );

    /**
     * What the chip actually renders.
     *
     * Existence and shape come from the transcript; counts, revert state and
     * conflicts come from the live changesets once they arrive. The index-keyed
     * map stays underneath as the fallback for turns recorded before batch ids
     * were written into the transcript.
     */
    const taskChipsByIndex = useMemo(() => {
        const map = new Map(batchesByMessageIndex);
        for (const [index, ref] of taskBatchRefsByIndex) {
            const live = ref.batch_ids.map(id => liveBatchById.get(id)).filter(Boolean);
            if (live.length) {
                map.set(index, {
                    batch_id: live[0].batch_id,
                    summary: live.reduce((s, b) => ({
                        created: s.created + b.summary.created,
                        updated: s.updated + b.summary.updated,
                        deleted: s.deleted + b.summary.deleted,
                    }), { created: 0, updated: 0, deleted: 0 }),
                    total: live.reduce((n, b) => n + b.total, 0),
                    active: live.reduce((n, b) => n + b.active, 0),
                    conflicted: live.some(b => b.conflicted),
                    // Never live-corrected: nothing was written, so nothing can
                    // later be reverted or drift.
                    noop: ref.noop,
                    skipped: ref.skipped,
                });
            } else {
                // Nothing live yet. Assume nothing was reverted — a batch that
                // was corrects itself the moment the query answers, and showing
                // the chip a beat early beats not showing it at all.
                map.set(index, {
                    batch_id: ref.batch_ids[0],
                    summary: { ...ref.summary },
                    total: ref.total,
                    active: ref.total,
                    conflicted: false,
                    noop: ref.noop,
                    skipped: ref.skipped,
                });
            }
        }
        return map;
    }, [batchesByMessageIndex, taskBatchRefsByIndex, liveBatchById]);

    // The outcomes that wrote nothing — see taskOutcomes.js for why they can
    // only be read out of the transcript.
    const taskOutcomeNotes = useMemo(() => collectTaskOutcomeNotes(messages), [messages]);

    // What the panel renders: the change log with those turns folded back in.
    const panelBatches = useMemo(
        () => mergeTaskOutcomeNotes(taskBatches, taskOutcomeNotes, currentSessionId),
        [taskBatches, taskOutcomeNotes, currentSessionId]
    );

    // The last *visible* turn, not the last array slot: persisted `tool` rows
    // are appended after the reply they belong to and render nothing, so a turn
    // firing two task tools would otherwise drop its "applying" state halfway.
    const lastVisibleIndex = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i]?.role !== 'tool') return i;
        }
        return -1;
    }, [messages]);

    // Index of the last user message in the conversation. Determines whether
    // a user message shows "Edit" (in-place) vs "Branch and edit".
    const lastUserMessageIndex = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i]?.role === 'user') return i;
        }
        return -1;
    }, [messages]);

    // Resolve accepted/stale status once per edit rather than once per render per
    // message, so memoized message rows keep a stable prop identity.
    const editedFilesWithStatus = useMemo(
        () => withEditStatus(editedFilesMap, files),
        [editedFilesMap, files]
    );
    const editedMockupsWithStatus = useMemo(
        () => withEditStatus(editedMockupsMap, files),
        [editedMockupsMap, files]
    );


    // Track initialization and mount status
    const hasInitialized = useRef(false);
    const isMounted = useRef(true);
    const scrollRef = useRef(null);
    const transcriptRef = useRef(null);
    const inputAreaRef = useRef(null);
    const abortRef = useRef(null);
    const userStoppedRef = useRef(false);
    // True while the reader is parked at the bottom. A ref, not state, so
    // scrolling never re-renders the page.
    const stickToBottomRef = useRef(true);
    const staggerCeilingRef = useRef(0);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null); // Hidden file input for uploads

    // File upload state
    const [uploadedFiles, setUploadedFiles] = useState([]); // Array of { id, name, content, type }
    const [isDragging, setIsDragging] = useState(false);

    // Track mount status to prevent state updates after unmount
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Initialize session ONLY once when sessions first load
    useEffect(() => {
        // Shared links have no session to resolve — the showcase playback owns
        // the transcript there.
        if (readOnly) {
            setSessionLoading(false);
            hasInitialized.current = true;
            return;
        }

        // The sessions list itself failed. Nothing below can run, and leaving
        // `sessionLoading` true would pin the skeleton on screen forever.
        if (sessionsQuery.isError) {
            setSessionLoading(false);
            setSessionError(describeError(sessionsQuery.error, 'Could not load your conversations.'));
            return;
        }

        if (sessionsQuery.isLoading || hasInitialized.current) return;

        const sessionId = searchParams.get('session');

        if (sessionId) {
            // URL has session ID - load it
            loadSession(sessionId);
            hasInitialized.current = true;
        } else if (sessions.length > 0) {
            // No session in URL, but sessions exist - load first one without triggering URL change yet
            loadSession(sessions[0].id, true);
            hasInitialized.current = true;
        } else if (sessions.length === 0 && !createSessionMutation.isPending) {
            // No sessions at all - create new one
            createNewSession();
            hasInitialized.current = true;
        }
    }, [readOnly, sessionsQuery.isLoading, sessionsQuery.isError, sessions.length]);

    // Handle URL changes (when user clicks session in sidebar)
    useEffect(() => {
        const sessionId = searchParams.get('session');
        if (sessionId && sessionId !== currentSessionId && hasInitialized.current) {
            loadSession(sessionId);
        }
    }, [searchParams]);

    /**
     * "Send to Advisor" — artifacts handed over from the Artifacts route.
     *
     * Route state rather than a query param, because the session effects above
     * call setSearchParams({ session }) with an object literal, which replaces
     * the whole query string and would drop a `?ref=` a beat after it arrived.
     *
     * Gated on the session having settled: loadSession and createNewSession both
     * setInput(draft || ''), so seeding any earlier gets overwritten. And it
     * appends rather than replaces, so a restored draft survives the handover.
     */
    useEffect(() => {
        const attach = location.state?.attachFiles;
        if (!attach?.length || readOnly || sessionLoading || !hasInitialized.current) return;

        setReferencedItems(prev => {
            const seen = new Set(prev.map(i => `${i.type}:${i.id}`));
            return [...prev, ...attach.filter(i => !seen.has(`${i.type}:${i.id}`))];
        });

        setInput(prev => {
            const tags = attach
                .map(f => `@[File: ${f.name}]`)
                .filter(tag => !prev.includes(tag));
            if (!tags.length) return prev;
            return (prev ? prev.trimEnd() + ' ' : '') + tags.join(' ') + ' ';
        });

        requestAnimationFrame(() => inputRef.current?.focusEnd?.());

        // Consume it, so a reload or a back-navigation doesn't re-attach.
        navigate(location.pathname + location.search, { replace: true, state: null });
    }, [location.state, location.pathname, location.search, readOnly, sessionLoading, navigate]);

    // Reset initialization when project changes (but skip in read-only mode)
    useEffect(() => {
        if (readOnly) {
            // In read-only mode, we always use mock messages, no need to reset
            return;
        }
        hasInitialized.current = false;
        setCurrentSessionId(null);
        setMessages([]);
        // Back to "we don't know yet" — the effect above will resolve it.
        setSessionLoading(true);
        setSessionError(null);
    }, [projectId, readOnly]);

    // ═══════════════════════════════════════════════════════════════════════════
    // SMART AUTOSCROLL
    //
    // Follow the conversation only while the reader is already at the bottom.
    // The old unconditional jump yanked you back down mid-read every time any
    // message state changed.
    // ═══════════════════════════════════════════════════════════════════════════
    const NEAR_BOTTOM_PX = 120;

    const isNearBottom = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    }, []);

    // True while the showcase follow loop owns scrollTop. Read inside scroll
    // handlers, so it has to be a ref rather than the derived boolean.
    const followModeRef = useRef(false);
    followModeRef.current = readOnly && !reduceMotion;

    const handleTranscriptScroll = useCallback(() => {
        const near = isNearBottom();
        // The follow loop writes scrollTop every frame, and during a long
        // catch-up — a mockup card mounting, say — the viewport is genuinely
        // far from the bottom for a moment. Reading that back as "the reader
        // scrolled away" would stop the demo dead a second in. In follow mode
        // only re-engaging happens here; disengaging is a wheel or a drag.
        if (!near && followModeRef.current) return;
        stickToBottomRef.current = near;
        // Only re-render on the transition, not on every scroll frame.
        setShowJumpToLatest(prev => (prev === !near ? prev : !near));
    }, [isNearBottom]);

    /** The reader has taken the wheel — stop following until asked back. */
    const releaseFollow = useCallback(() => {
        if (!stickToBottomRef.current) return;
        stickToBottomRef.current = false;
        setShowJumpToLatest(true);
    }, []);

    // A programmatic smooth scroll also fires `scroll`, which can swallow an
    // upward flick. Wheel direction is unambiguous intent.
    const handleTranscriptWheel = useCallback((e) => {
        if (e.deltaY < 0) releaseFollow();
    }, [releaseFollow]);

    // A finger on the transcript is the touch equivalent of that wheel. Without
    // it, follow mode would have no way to disengage on a phone, since the
    // scroll handler above deliberately stops unsticking there.
    const handleTranscriptTouchMove = useCallback(() => {
        if (followModeRef.current) releaseFollow();
        else handleTranscriptScroll();
    }, [releaseFollow, handleTranscriptScroll]);

    const jumpToLatest = useCallback(() => {
        stickToBottomRef.current = true;
        setShowJumpToLatest(false);
        // In follow mode the rAF loop is already easing toward the bottom;
        // adding a native smooth scroll means two writers fighting over the
        // same property in the same frame.
        if (followModeRef.current) return;
        scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: reduceMotion ? 'auto' : 'smooth',
        });
    }, [reduceMotion]);

    // Depend on length, not the array: the array identity also changes when the
    // invisible persisted `tool` message is appended, which should not animate.
    // The showcase runs its own follow loop below; these two would fight it,
    // each writing scrollTop from a different scheduler in the same frame.
    const eventDrivenScroll = !readOnly;

    useEffect(() => {
        const el = scrollRef.current;
        if (!eventDrivenScroll || !el || !stickToBottomRef.current) return;
        el.scrollTo({ top: el.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, [eventDrivenScroll, messages.length, loading, reduceMotion]);

    // Result cards mount seconds after their message and attachment images
    // resize on load. Re-pin instantly (never smoothly — a smooth scroll
    // retriggered on every observer callback stutters badly).
    useEffect(() => {
        const el = scrollRef.current;
        const content = transcriptRef.current;
        if (!eventDrivenScroll || !el || !content || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => {
            if (stickToBottomRef.current) el.scrollTop = el.scrollHeight;
        });
        ro.observe(content);
        return () => ro.disconnect();
    }, [eventDrivenScroll, currentSessionId, messages.length === 0]);

    // ═══════════════════════════════════════════════════════════════════════════
    // SHOWCASE FOLLOW LOOP
    //
    // The demo has to be watchable hands-off, and the event-driven scrolling
    // above cannot deliver that. It fires on discrete events — a message lands,
    // an observer reports a resize — while the transcript grows continuously
    // underneath: markdown reflows, tool cards mount, a Sandpack preview
    // settles, a skeleton swaps for a real card. Every one of those is a
    // separate jump, and a `behavior: 'smooth'` scroll restarted mid-flight
    // stutters or gives up entirely.
    //
    // So: one rAF loop that re-reads the target every frame and eases toward
    // it. Continuous growth becomes continuous motion, and there is exactly one
    // writer of scrollTop.
    // ═══════════════════════════════════════════════════════════════════════════
    useEffect(() => {
        if (!readOnly || reduceMotion) return undefined;
        const el = scrollRef.current;
        if (!el) return undefined;

        let raf = requestAnimationFrame(function step() {
            raf = requestAnimationFrame(step);
            if (!stickToBottomRef.current) return;

            const target = el.scrollHeight - el.clientHeight;
            const delta = target - el.scrollTop;
            if (delta <= 0) return;

            // `scrollHeight` and `clientHeight` are rounded, so the last few
            // pixels of the target are noise the ease can never land on — it
            // just multiplies them by 0.14 forever. Snap them instead. Above
            // that, the ease keeps a 40px reflow gentle and still catches a
            // 600px card mounting within a few frames.
            el.scrollTop = delta < 4 ? target : el.scrollTop + delta * 0.14;
        });

        return () => cancelAnimationFrame(raf);
    }, [readOnly, reduceMotion, showcase]);

    // ═══════════════════════════════════════════════════════════════════════════
    // SHOWCASE PLAYBACK
    //
    // Driving the same setters a live turn writes, so the demo lights up the
    // working state, the tool skeletons, the task chip and every artifact card
    // through production code paths rather than a parallel renderer.
    //
    // Playing from an empty transcript is also what fixes the old landing spot:
    // the conversation grows downward under a reader already parked at the top,
    // instead of dumping them at the end of something they never read.
    // ═══════════════════════════════════════════════════════════════════════════
    const jumpToTop = useCallback(() => {
        stickToBottomRef.current = false;
        setShowJumpToLatest(true);
        scrollRef.current?.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    }, [reduceMotion]);

    // Replay re-arms the follow loop: whoever hit it wants to watch, not read.
    const resumeFollow = useCallback(() => {
        stickToBottomRef.current = true;
        setShowJumpToLatest(false);
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, []);

    // Setters are stable across renders, so this object is built once and the
    // playback effect never restarts mid-conversation.
    const showcaseActions = useMemo(() => ({
        setMessages,
        setLoading,
        setTurnStartedAt,
        setActiveTurnContext,
        setAnnouncedTools,
        setTurnMetaByIndex,
        setPendingToolsByIndex,
        onJumpToTop: jumpToTop,
        onResumeFollow: resumeFollow,
    }), [jumpToTop, resumeFollow]);

    const showcasePlayback = useShowcasePlayback({
        enabled: readOnly,
        script: showcase,
        reduceMotion,
        actions: showcaseActions,
    });

    // Reset panel state when switching conversations - a changeset belongs to
    // the session that produced it.
    useEffect(() => {
        closePanel('tasks');
        setFocusBatchId(null);
        setTaskPanelTab('changes');
    }, [currentSessionId, closePanel]);

    // Rehydrate agent state (cards) from message history
    useEffect(() => {
        if (!messages.length) return;

        const newCreatedMap = {};
        const newEditedMap = {};
        const newCreatedMockupsMap = {};
        const newEditedMockupsMap = {};
        let hasNewData = false;

        messages.forEach((msg, index) => {
            if (msg.role === 'model' && msg.tool_calls) {
                // The persisted tool outputs for this turn — every `tool` row
                // that follows it, not just the first. A turn that rewrote a
                // document *and* touched tasks has one row each, and matching
                // by name below is what keeps the document from reading the
                // task row and losing its diff.
                const toolRows = collectToolRows(messages, index);

                msg.tool_calls.forEach(tool => {
                    const toolResult = takeToolRow(toolRows, tool.tool_name);

                    // A reply is committed to `messages` before the rows its
                    // tools produce, so this effect runs at least once for the
                    // turn with nothing to read and writes the placeholder
                    // branches below. Without a way back, that placeholder is
                    // final: the card keeps "Edited (from history)" and never
                    // gets its diff, because the real result arrives on a later
                    // pass that the `already present` guards skip.
                    // Narrowly scoped to this effect's OWN placeholder: a live
                    // turn writes its card directly from the tool's response,
                    // with content this branch never sees, and overwriting that
                    // would lose it.
                    const canUpgrade = !!(toolResult?.success && toolResult.result);
                    const needsEntry = (existing) => !existing || (canUpgrade && existing.isPlaceholder);

                    // Rehydrate Created Documents
                    if (tool.tool_name === 'create_document') {
                        if (!createdFilesMap[index] || (canUpgrade && createdFilesMap[index].id === 'history')) {
                            // Try to get content from tool result if available (more reliable), otherwise input args
                            const content = toolResult?.result?.content || tool.arguments.content;

                            newCreatedMap[index] = {
                                id: toolResult?.result?.file_id || 'history',
                                name: tool.arguments.name,
                                category: tool.arguments.category || 'Docs',
                                content: content
                            };
                            hasNewData = true;
                        }
                    }

                    // Rehydrate Edited Documents
                    const editTools = ['rewrite_document', 'insert_in_document', 'replace_in_document'];
                    if (editTools.includes(tool.tool_name)) {
                        if (needsEntry(editedFilesMap[index])) {
                            let type = 'edit';
                            if (tool.tool_name === 'rewrite_document') type = 'rewrite';
                            if (tool.tool_name === 'insert_in_document') type = 'insert';
                            if (tool.tool_name === 'replace_in_document') type = 'replace';

                            // If we have a tool result, we can fully rehydrate!
                            if (toolResult && toolResult.success && toolResult.result) {
                                const resultData = toolResult.result;
                                newEditedMap[index] = {
                                    file: {
                                        name: resultData.file_name,
                                        id: resultData.file_id
                                    },
                                    originalContent: resultData.original_content,
                                    modifiedContent: resultData.modified_content, // Store for isAccepted check
                                    editType: resultData.edit_type || type,
                                    editSummary: resultData.edit_summary || 'Edited',
                                    isLoading: false,
                                    diffData: resultData, // Store full data for compatibility
                                    isPersisted: true
                                };
                            } else {
                                // Fallback for legacy/incomplete history (loading state or just generic)
                                newEditedMap[index] = {
                                    file: {
                                        name: tool.arguments.file_name,
                                        id: tool.arguments.file_id
                                    },
                                    editType: type,
                                    editSummary: 'Edited (from history)',
                                    isLoading: false,
                                    originalContent: null,
                                    isPlaceholder: true
                                };
                            }
                            hasNewData = true;
                        }
                    }

                    // Rehydrate Created Mockups
                    if (tool.tool_name === 'create_mockup') {
                        if (!createdMockupsMap[index] || (canUpgrade && createdMockupsMap[index].id === 'history')) {
                            const content = toolResult?.result?.content || tool.arguments.content;
                            newCreatedMockupsMap[index] = {
                                id: toolResult?.result?.file_id || 'history',
                                name: tool.arguments.name,
                                category: 'Mockups',
                                type: 'mockup',
                                content: content
                            };
                            hasNewData = true;
                        }
                    }

                    // Rehydrate Edited Mockups
                    const mockupEditTools = ['rewrite_mockup', 'insert_in_mockup', 'replace_in_mockup'];
                    if (mockupEditTools.includes(tool.tool_name)) {
                        if (needsEntry(editedMockupsMap[index])) {
                            let type = 'edit';
                            if (tool.tool_name === 'rewrite_mockup') type = 'rewrite';
                            if (tool.tool_name === 'insert_in_mockup') type = 'insert';
                            if (tool.tool_name === 'replace_in_mockup') type = 'replace';

                            if (toolResult && toolResult.success && toolResult.result) {
                                const resultData = toolResult.result;
                                newEditedMockupsMap[index] = {
                                    file: {
                                        name: resultData.file_name,
                                        id: resultData.file_id,
                                        type: 'mockup'
                                    },
                                    originalContent: resultData.original_content,
                                    modifiedContent: resultData.modified_content,
                                    editType: resultData.edit_type || type,
                                    editSummary: resultData.edit_summary || 'Edited',
                                    isLoading: false,
                                    diffData: resultData,
                                    isPersisted: true
                                };
                            } else {
                                newEditedMockupsMap[index] = {
                                    file: {
                                        name: tool.arguments.file_name,
                                        id: tool.arguments.file_id,
                                        type: 'mockup'
                                    },
                                    editType: type,
                                    editSummary: 'Edited (from history)',
                                    isLoading: false,
                                    originalContent: null,
                                    isPlaceholder: true
                                };
                            }
                            hasNewData = true;
                        }
                    }
                });
            }
        });

        if (hasNewData) {
            setCreatedFilesMap(prev => ({ ...prev, ...newCreatedMap }));
            setEditedFilesMap(prev => ({ ...prev, ...newEditedMap }));
            setCreatedMockupsMap(prev => ({ ...prev, ...newCreatedMockupsMap }));
            setEditedMockupsMap(prev => ({ ...prev, ...newEditedMockupsMap }));
        }
    }, [messages]);

    // Handle content changes from the agent editor panel (auto-save)
    // NOTE: This useCallback MUST be declared before any conditional returns to satisfy React hooks rules
    // PATTERN: Matches Workspace.js - update local state immediately, debounce API save

    // Debounced save for API calls only (same pattern as Workspace.js)
    const saveAgentFileContent = useCallback(
        debounce(async (fileId, content) => {
            // Skip API call for history/transient files
            if (fileId === 'history') return;

            setIsSavingFile(true);
            try {
                await api.put(`/files/${fileId}`, { content });
            } catch (error) {
                console.error('Auto-save failed:', error);
            } finally {
                setIsSavingFile(false);
            }
        }, 1000),
        []
    );

    // Handler called on every content change
    const handleAgentFileContentChange = useCallback((fileId, newContent) => {
        if (!fileId) return;

        // Update local state immediately for smooth typing
        setAgentFile(prev => prev ? { ...prev, content: newContent } : null);

        // Also update in createdFilesMap if this file exists there
        setCreatedFilesMap(prev => {
            const newMap = { ...prev };
            for (const key in newMap) {
                if (newMap[key]?.id === fileId) {
                    newMap[key] = { ...newMap[key], content: newContent };
                }
            }
            return newMap;
        });

        // Debounced API save
        saveAgentFileContent(fileId, newContent);
    }, [saveAgentFileContent]);

    const getModelIcon = (preset) => {
        switch (preset) {
            case 'powerful': return <Brain className="h-3.5 w-3.5" />;
            case 'fast': return <Zap className="h-3.5 w-3.5" />;
            case 'efficient': return <Leaf className="h-3.5 w-3.5" />;
            default: return <Zap className="h-3.5 w-3.5" />;
        }
    };

    const getModelColor = (preset) => {
        switch (preset) {
            case 'powerful': return 'text-purple-400';
            case 'fast': return 'text-accent';
            case 'efficient': return 'text-green-400';
            default: return 'text-accent';
        }
    };

    const hasUserMsg = (msgs) => Array.isArray(msgs) && msgs.some(m => m.role === 'user');

    const loadSession = async (sessionId, skipUrlUpdate = false) => {
        if (!isMounted.current) return;

        // Save current input to drafts map before switching
        const currentKey = currentSessionId || 'new';
        setDraftsMap(prev => ({ ...prev, [currentKey]: input }));

        // Cleanup empty unused session if switching away from a session with 0 user messages and no draft
        if (currentSessionId && currentSessionId !== sessionId && !hasUserMsg(messages) && (!input || !input.trim())) {
            deleteSessionMutation.mutate(currentSessionId);
        }

        setSessionLoading(true);
        setSessionError(null);
        try {
            const res = await api.get(`/chat-sessions/${sessionId}`);

            // Only update state if still mounted
            if (!isMounted.current) return;

            setCurrentSessionId(sessionId);
            const loaded = res.data.messages || [];

            // Stagger the entrance of a whole conversation arriving at once.
            // Reset to 0 afterwards so a single appended reply — the one the
            // user just waited for — animates in immediately.
            staggerCeilingRef.current = loaded.length;
            stickToBottomRef.current = true;
            setShowJumpToLatest(false);
            setTurnMetaByIndex({});
            setPendingToolsByIndex({});
            setCreatedFilesMap({});
            setEditedFilesMap({});
            setCreatedMockupsMap({});
            setEditedMockupsMap({});
            setEditingMessageIndex(null);
            setEditingMessageText('');

            setMessages(loaded);

            // Restore draft text for target session if present
            const targetDraft = draftsMap[sessionId] || '';
            setInput(targetDraft);

            // Update URL without triggering navigation - only if still mounted
            if (!skipUrlUpdate && isMounted.current) {
                setSearchParams({ session: sessionId }, { replace: true });
            }
        } catch (error) {
            console.error(error);
            if (isMounted.current) {
                const described = { ...describeError(error, 'Could not load this conversation.'), sessionId };
                setSessionError(described);
                // The card below only renders over an empty column. If the load
                // failed while switching *away* from a conversation, the old one
                // is still on screen and unchanged — there is nothing to render
                // the card into, so that case gets the toast instead.
                if (hasUserMsg(messages)) {
                    toast.error(described.title, { description: described.detail });
                }
            }
        } finally {
            if (isMounted.current) {
                setSessionLoading(false);
            }
        }
    };

    const createNewSession = async () => {
        if (!isMounted.current) return;

        // A new chat is empty from the first frame, so there is nothing to wait
        // for — show the hero immediately rather than a skeleton of content
        // that will never arrive.
        setSessionLoading(false);
        setSessionError(null);

        // Save current input to drafts map
        const currentKey = currentSessionId || 'new';
        setDraftsMap(prev => ({ ...prev, [currentKey]: input }));

        // If current session has no user messages, don't create duplicate backend session
        if (currentSessionId && !hasUserMsg(messages)) {
            const newDraft = draftsMap['new'] || input || '';
            setInput(newDraft);
            return;
        }

        // Cleanup previous empty session if user switches to New Chat without typing
        if (currentSessionId && !hasUserMsg(messages) && (!input || !input.trim())) {
            deleteSessionMutation.mutate(currentSessionId);
        }

        try {
            const newSession = await createSessionMutation.mutateAsync();

            // Only update state if still mounted
            if (!isMounted.current) return;

            setCurrentSessionId(newSession.id);
            setCreatedFilesMap({});
            setEditedFilesMap({});
            setCreatedMockupsMap({});
            setEditedMockupsMap({});
            setEditingMessageIndex(null);
            setEditingMessageText('');
            setMessages(newSession.messages || []);

            // Restore draft input for new chat if present
            const newDraft = draftsMap['new'] || '';
            setInput(newDraft);

            setSearchParams({ session: newSession.id }, { replace: true });
        } catch (error) {
            console.error(error);
            if (isMounted.current) {
                toast.error("Failed to create chat session");
            }
        }
    };

    const confirmDeleteSession = async () => {
        if (!deleteConfirmSession) return;
        const sessionId = deleteConfirmSession;

        try {
            await deleteSessionMutation.mutateAsync(sessionId);

            if (sessionId === currentSessionId) {
                const remaining = sessions.filter(s => s.id !== sessionId);
                if (remaining.length > 0) {
                    loadSession(remaining[0].id);
                } else {
                    createNewSession();
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setDeleteConfirmSession(null);
        }
    };

    const togglePin = async (sessionId, currentPinned, e) => {
        e.stopPropagation();
        try {
            await updateSessionMutation.mutateAsync({ sessionId, updates: { pinned: !currentPinned } });
            toast.success(currentPinned ? "Unpinned" : "Pinned");
        } catch (error) {
            toast.error("Failed to update");
        }
    };

    // `opts.text` lets callers send content that is not (yet) in `input` state.
    // Quick prompts, Retry and Edit & resend all go through it: relying on
    // setInput + setTimeout captured a stale closure where `input` was still ''.
    const handleSend = async (e, opts = {}) => {
        e?.preventDefault();

        const source = opts.text ?? input;

        // Resolve inline references in the input
        let finalContent = source;
        let hasContent = source.trim().length > 0;
        const usedSelections = [];

        // Find all reference tags in the input: @[File: name:L1-10]
        if (attachedSelections.length > 0) {
            attachedSelections.forEach(sel => {
                const tag = `@[File: ${sel.file.name}${sel.range ? `:${sel.range.startLineNumber}-${sel.range.endLineNumber}` : ''}]`;
                if (finalContent.includes(tag)) {
                    // Append the actual code content at the end of the message
                    // We keep the tag in the text as a reference point, but add the code block at the end
                    usedSelections.push(`> [Referenced ${sel.file.name}${sel.range ? `:${sel.range.startLineNumber}-${sel.range.endLineNumber}` : ''}]\n> ${sel.text}`);
                    hasContent = true;
                }
            });
        }

        if (!hasContent || loading || !currentSessionId) return;

        // Fail fast rather than spending the browser's network timeout to learn
        // the same thing. Returning here leaves the text in the composer, so
        // nothing is lost. (`navigator.onLine` is only trusted when false —
        // see useOnlineStatus.)
        if (!isOnline) {
            toast.error("You're offline", { description: 'Reconnect to send this message.' });
            return;
        }

        // Append resolved selections to the message content
        if (usedSelections.length > 0) {
            finalContent += `\n\n${usedSelections.join('\n\n')}`;
        }

        // Split uploaded files into images vs text files BEFORE clearing state
        const filesToUpload = [...uploadedFiles];

        // Separate images from text files
        const imageFiles = filesToUpload.filter(f => f.type === 'image');
        const textFiles = filesToUpload.filter(f => f.type !== 'image');

        // Store images with user message for display (keep dataUrl for preview)
        const imageAttachments = imageFiles.map(img => ({
            name: img.name,
            type: 'image',
            mimeType: img.mimeType,
            dataUrl: img.dataUrl // Keep the full dataUrl for display
        }));

        // Store text file references for display (just name and type)
        const fileAttachments = textFiles.map(f => ({
            name: f.name,
            type: f.type, // 'code' or 'doc'
            size: f.size
        }));

        // Combine all attachments (including any preserved when editing a message)
        const preservedAttachments = Array.isArray(opts.preserveAttachments) ? opts.preserveAttachments : [];
        const messageAttachments = [...preservedAttachments, ...imageAttachments, ...fileAttachments];

        // Where this turn lands. `opts.replaceFrom` is a retry/edit overwriting the
        // turn that used to start there; otherwise we append after dropping any
        // trailing stopped/failed marker, so sending again clears the last
        // failure instead of stacking beneath it.
        //
        // Computed here, not inside the updater, because the index math below
        // needs it synchronously — and derived from the render-scope array for
        // the same reason `messages.length` was.
        const base = opts.replaceFrom != null
            ? messages.slice(0, opts.replaceFrom)
            : trimTrailingMarkers(messages);
        const baseLength = base.length;

        // Collect referenced files and tasks across the entire conversation up to `base`
        // plus the current message and `@` mentions.
        const convRefs = collectConversationReferences(base, finalContent, referencedItems, files, tasks);
        const referencedFiles = [...convRefs.fileIds];
        const referencedTasks = [...convRefs.taskIds];

        const userMsg = {
            role: 'user',
            content: finalContent,
            timestamp: new Date().toISOString(),
            attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
            referenced_files: referencedFiles.length > 0 ? referencedFiles : undefined,
            referenced_tasks: referencedTasks.length > 0 ? referencedTasks : undefined,
        };
        // Anything appended from here is a live turn, not a bulk load — no
        // stagger delay on the reply the user is actively waiting for.
        staggerCeilingRef.current = 0;
        stickToBottomRef.current = true;
        setShowJumpToLatest(false);

        setMessages([...base, userMsg]);
        if (!opts.preserveComposer) {
            setInput('');
            setAttachedSelections([]);
            setUploadedFiles([]);
            setReferencedItems([]);
        }
        setLoading(true);

        // Snapshot what is genuinely going into this turn BEFORE clearing the
        // references, so the working state can name it instead of spinning.
        const turnStart = Date.now();
        setTurnStartedAt(turnStart);
        setAnnouncedTools(null);
        setActiveTurnContext({
            modelLabel: MODEL_LABELS[modelPreset] || modelPreset,
            files: isFullContext
                ? files.map(f => f.name)
                : convRefs.fileNames,
            tasks: convRefs.taskTitles,
            fullContext: isFullContext,
            webSearch: isWebSearch,
            imageCount: imageFiles.length,
        });

        // Prepare images for API (extract base64 data from dataUrl)
        const preservedImages = preservedAttachments
            .filter(att => att?.type === 'image' && typeof att.dataUrl === 'string' && att.dataUrl.includes(','))
            .map(img => ({
                name: img.name,
                mimeType: img.mimeType,
                data: img.dataUrl.split(',')[1]
            }));
        const attachedImages = [
            ...preservedImages,
            ...imageFiles.map(img => ({
                name: img.name,
                mimeType: img.mimeType,
                data: img.dataUrl.split(',')[1] // Remove "data:image/png;base64," prefix
            }))
        ];

        // Save text files to project and add to references
        for (const file of textFiles) {
            try {
                const result = await createFileMutation.mutateAsync({
                    name: file.name,
                    type: file.type,
                    category: file.type === 'code' ? 'Code' : 'Docs',
                    content: file.content,
                    tags: ['ai-chat'] // Mark as chat-uploaded file
                });
                if (result?.id) {
                    referencedFiles.push(result.id);
                }
            } catch (err) {
                console.error(`Failed to upload ${file.name}:`, err);
                toast.error(`Failed to upload ${file.name}`);
            }
        }

        // One controller per turn, threaded through every request the turn makes
        // so Stop breaks the whole chain rather than just the first call.
        const controller = new AbortController();
        abortRef.current = controller;
        userStoppedRef.current = false;
        let newAiMsgIndex = null;

        try {
            // A dropped marker means the persisted array may still hold the turn
            // it stood for — a stop leaves the server's own reply behind, since
            // aborting the request never cancelled the handler. Trimming to the
            // same length here is what keeps `newAiMsgIndex` meaning the same
            // slot on both sides; without it every index-keyed thing (task
            // changesets, a later retry's cut point) drifts by one from here on.
            if (base.length !== messages.length && opts.replaceFrom == null) {
                try {
                    await truncateMessagesMutation.mutateAsync({
                        sessionId: currentSessionId,
                        fromIndex: base.length,
                    });
                } catch (err) {
                    // Not fatal — the message still sends. Logged because the
                    // indices are now approximate until the next full load.
                    console.error('Could not realign session before send:', err);
                }
            }

            const res = await sendMessageMutation.mutateAsync({
                sessionId: currentSessionId,
                message: userMsg.content,
                contextMode: isFullContext ? 'all' : 'selective',
                referencedFiles,
                referencedTasks,
                attachedImages, // NEW: Pass images directly to AI
                webSearch: isWebSearch,
                modelPreset,
                signal: controller.signal
            });

            const aiMessage = res.ai_message;
            setMessages(prev => [...prev, aiMessage]);

            // The turn occupies the two slots after `base`: the user message at
            // baseLength, the reply right after it. Every per-message map below
            // is keyed by those, so they have to be exact — reading
            // `messages.length` here instead would be a render-scope snapshot
            // taken before the optimistic append, and wrong by a whole turn on
            // any retry that replaced one.
            newAiMsgIndex = baseLength + 1;

            // The reply is here, so its round-trip time is a real measurement.
            setTurnMetaByIndex(prev => ({
                ...prev,
                [newAiMsgIndex]: {
                    modelLabel: MODEL_LABELS[modelPreset] || modelPreset,
                    elapsedMs: Date.now() - turnStart,
                },
            }));

            // ═══════════════════════════════════════════════════════════════════
            // AUTO-EXECUTE DOCUMENT TOOLS - Seamless document creation
            // ═══════════════════════════════════════════════════════════════════
            // ═══════════════════════════════════════════════════════════════════
            // TASK CHANGES — already applied, arriving with the reply
            //
            // These used to be executed here, one HTTP call at a time, after the
            // reply had been persisted. That put the whole batch inside the
            // tab's lifetime: navigate away mid-loop and the writes were lost
            // while the transcript went on claiming them. The server now applies
            // them inside the turn, so this is display only.
            // ═══════════════════════════════════════════════════════════════════
            const serverTaskBatches = aiMessage.task_batches || [];
            if (serverTaskBatches.length > 0) {
                seedTaskChangesets(serverTaskBatches);
                announceTaskOutcome(serverTaskBatches);

                // Mixed turn: the document takes the surface. The task work is
                // still seeded, still chipped in the stream and still one tap
                // away in the panel switcher — it just doesn't get opened and
                // then yanked away half a second later by the editor.
                const documentAlsoRan = (aiMessage.tool_calls || [])
                    .some(tc => SURFACE_CLAIMING_TOOLS.has(tc.tool_name));
                if (!documentAlsoRan) {
                    openTaskPanel({ tab: 'changes', batchId: serverTaskBatches[0].batch_id });
                }
            }

            if (aiMessage.tool_calls && aiMessage.tool_calls.length > 0) {
                // Announce every tool immediately. Each one still costs another
                // round-trip (document edits cost one or two more model calls),
                // so showing what is queued turns that gap into visible progress.
                // Task tools open as `done`: their work landed before this reply
                // did, so a spinner over them would be theatre.
                let firstPending = true;
                const announced = aiMessage.tool_calls.map((tc) => {
                    if (TASK_TOOLS.includes(tc.tool_name)) {
                        return {
                            toolName: tc.tool_name,
                            target: describeToolTarget(tc),
                            status: tc.status === 'error' ? 'failed' : 'done',
                        };
                    }
                    const status = firstPending ? 'running' : 'queued';
                    firstPending = false;
                    return { toolName: tc.tool_name, target: describeToolTarget(tc), status };
                });
                setAnnouncedTools(announced);
                setPendingToolsByIndex(prev => ({ ...prev, [newAiMsgIndex]: announced }));

                let toolCursor = 0;
                for (const toolCall of aiMessage.tool_calls) {
                    // A stop between tools must not start the next one.
                    if (controller.signal.aborted) break;

                    // Advance the skeletons: everything before this one has
                    // landed as a real card, this one is in flight, the rest
                    // stay queued. Done rows are filtered out when rendering,
                    // so a skeleton is never shown beside its finished card.
                    // The cursor still advances over every tool so these indices
                    // line up with the announced rows, but task rows keep the
                    // status they were announced with: their work finished
                    // server-side before this loop existed, and re-marking one
                    // `running` here would put a spinner on a completed change.
                    const cursor = toolCursor++;
                    setPendingToolsByIndex(prev => {
                        const rows = prev[newAiMsgIndex];
                        if (!rows) return prev;
                        return {
                            ...prev,
                            [newAiMsgIndex]: rows.map((t, i) => (
                                TASK_TOOLS.includes(t.toolName) ? t : {
                                    ...t,
                                    status: i < cursor ? 'done' : i === cursor ? 'running' : 'queued',
                                }
                            )),
                        };
                    });

                    if (toolCall.tool_name === 'create_document') {
                        try {
                            // Auto-execute the tool to create the document
                            const result = await executeToolMutation.mutateAsync({
                                toolName: toolCall.tool_name,
                                arguments: toolCall.arguments,
                                signal: controller.signal
                            });

                            if (result.success && result.result) {
                                const createdFile = {
                                    id: result.result.file_id,
                                    name: result.result.name,
                                    category: result.result.category,
                                    content: result.result.content
                                };

                                // Store in map for persistent card display
                                setCreatedFilesMap(prev => ({
                                    ...prev,
                                    [newAiMsgIndex]: createdFile
                                }));

                                // Set as current agent file and open editor
                                setAgentFile(createdFile);
                                setActivePanel('editor');
                                setShowSidebar(false); // Collapse sidebar for more space

                                toast.success(`Created: ${result.result.name}`, {
                                    description: 'Edit in the panel or continue chatting with the AI'
                                });

                                try {
                                    const toolMessage = {
                                        role: 'tool',
                                        content: {
                                            success: true,
                                            tool_name: toolCall.tool_name,
                                            result: {
                                                file_id: result.result.file_id,
                                                name: result.result.name,
                                                category: result.result.category,
                                                content: result.result.content
                                            }
                                        },
                                        timestamp: new Date().toISOString()
                                    };
                                    setMessages(prev => [...prev, toolMessage]);
                                    await api.post(`/chat-sessions/${currentSessionId}/messages/raw`, toolMessage);
                                } catch (persistError) {
                                    console.error("Failed to persist tool output:", persistError);
                                }
                            }
                        } catch (error) {
                            // Propagate to the outer catch: a stop is not a failure,
                            // and must abandon the remaining tools.
                            if (isAbortError(error)) throw error;
                            console.error('Auto-execute failed:', error);
                            toast.error('Failed to create document', { description: describeError(error).detail });
                        }
                    }

                    // Task tools already ran, server-side, inside the turn that
                    // produced this reply — see the handling above the loop.
                    // There is nothing left to execute and nothing to persist.
                    if (TASK_TOOLS.includes(toolCall.tool_name)) continue;

                    // ═══════════════════════════════════════════════════════════════
                    // DOCUMENT EDITING TOOLS - Multi-step with diff preview
                    // ═══════════════════════════════════════════════════════════════
                    const editTools = ['rewrite_document', 'insert_in_document', 'replace_in_document'];
                    if (editTools.includes(toolCall.tool_name)) {
                        // Determine loading step text based on tool
                        const getLoadingStep = () => {
                            switch (toolCall.tool_name) {
                                case 'rewrite_document': return 'Rewriting document...';
                                case 'insert_in_document': return 'Analyzing and inserting content...';
                                case 'replace_in_document': return 'Finding and replacing content...';
                                default: return 'Processing...';
                            }
                        };

                        // Set loading state in card immediately
                        setEditedFilesMap(prev => ({
                            ...prev,
                            [newAiMsgIndex]: {
                                file: { name: toolCall.arguments.file_name },
                                isLoading: true,
                                loadingStep: getLoadingStep()
                            }
                        }));

                        try {
                            // Execute the multi-step edit
                            const result = await editDocumentMutation.mutateAsync({
                                toolName: toolCall.tool_name,
                                fileId: toolCall.arguments.file_id,
                                fileName: toolCall.arguments.file_name,
                                instructions: toolCall.arguments.instructions,
                                signal: controller.signal
                            });

                            if (result.success && result.result) {
                                const editResult = result.result;
                                const editedFile = {
                                    id: editResult.file_id,
                                    name: editResult.file_name,
                                    category: 'Docs',
                                    content: editResult.modified_content
                                };

                                // Update card with completed state
                                setEditedFilesMap(prev => ({
                                    ...prev,
                                    [newAiMsgIndex]: {
                                        file: editedFile,
                                        originalContent: editResult.original_content,
                                        editType: editResult.edit_type,
                                        editSummary: editResult.edit_summary,
                                        isLoading: false
                                    }
                                }));

                                // Open diff view
                                setAgentFile(editedFile);
                                setOriginalContent(editResult.original_content);
                                setEditSummary(editResult.edit_summary);
                                setIsDiffMode(true);
                                setActivePanel('editor');
                                setShowSidebar(false);

                                toast.success(`Changes ready for review`, {
                                    description: editResult.edit_summary
                                });

                                // ══════════════════════════════════════════════════════════
                                // PERSISTENCE: Save the tool result to chat history
                                // ══════════════════════════════════════════════════════════
                                try {
                                    // Manually append a tool message
                                    const toolMessage = {
                                        role: 'tool',
                                        content: {
                                            success: true,
                                            tool_name: toolCall.tool_name,
                                            result: {
                                                file_id: editResult.file_id,
                                                file_name: editResult.file_name,
                                                original_content: editResult.original_content,
                                                modified_content: editResult.modified_content,
                                                edit_type: editResult.edit_type,
                                                edit_summary: editResult.edit_summary
                                            }
                                        },
                                        timestamp: new Date().toISOString()
                                    };

                                    // Add to local state (optional, but good for consistency immediately)
                                    setMessages(prev => [...prev, toolMessage]);

                                    // Persist to backend using the raw message endpoint
                                    await api.post(`/chat-sessions/${currentSessionId}/messages/raw`, toolMessage);

                                } catch (persistError) {
                                    console.error("Failed to persist tool output:", persistError);
                                }
                            }
                        } catch (error) {
                            // Propagate to the outer catch: a stop is not a failure,
                            // and must abandon the remaining tools.
                            if (isAbortError(error)) throw error;
                            console.error('Document edit failed:', error);
                            // Update card to show error
                            setEditedFilesMap(prev => ({
                                ...prev,
                                [newAiMsgIndex]: {
                                    ...prev[newAiMsgIndex],
                                    isLoading: false,
                                    loadingStep: 'Failed to edit document'
                                }
                            }));
                            toast.error('Failed to edit document', { description: describeError(error).detail });
                        }
                    }

                    // ═══════════════════════════════════════════════════════════════
                    // UI MOCKUP CREATION TOOL - Seamless mockup creation
                    // ═══════════════════════════════════════════════════════════════
                    if (toolCall.tool_name === 'create_mockup') {
                        try {
                            // Auto-execute the tool to create the mockup
                            const result = await executeToolMutation.mutateAsync({
                                toolName: toolCall.tool_name,
                                arguments: toolCall.arguments,
                                signal: controller.signal
                            });

                            if (result.success && result.result) {
                                const createdMockup = {
                                    id: result.result.file_id,
                                    name: result.result.name,
                                    category: result.result.category,
                                    type: 'mockup',
                                    content: result.result.content
                                };

                                // Store in map for persistent card display
                                setCreatedMockupsMap(prev => ({
                                    ...prev,
                                    [newAiMsgIndex]: createdMockup
                                }));

                                // Open the mockup preview panel
                                setMockupPanelFile(createdMockup);
                                setActivePanel('mockup');
                                setShowSidebar(false); // Collapse sidebar for more space

                                toast.success(`Created mockup: ${result.result.name}`, {
                                    description: 'Preview and edit in the panel on the right'
                                });

                                try {
                                    const toolMessage = {
                                        role: 'tool',
                                        content: {
                                            success: true,
                                            tool_name: toolCall.tool_name,
                                            result: {
                                                file_id: result.result.file_id,
                                                name: result.result.name,
                                                category: result.result.category,
                                                content: result.result.content
                                            }
                                        },
                                        timestamp: new Date().toISOString()
                                    };
                                    setMessages(prev => [...prev, toolMessage]);
                                    await api.post(`/chat-sessions/${currentSessionId}/messages/raw`, toolMessage);
                                } catch (persistError) {
                                    console.error("Failed to persist tool output:", persistError);
                                }
                            }
                        } catch (error) {
                            // Propagate to the outer catch: a stop is not a failure,
                            // and must abandon the remaining tools.
                            if (isAbortError(error)) throw error;
                            console.error('Mockup creation failed:', error);
                            toast.error('Failed to create mockup', { description: describeError(error).detail });
                        }
                    }

                    // ═══════════════════════════════════════════════════════════════
                    // UI MOCKUP EDITING TOOLS - Multi-step with diff preview
                    // ═══════════════════════════════════════════════════════════════
                    const mockupEditTools = ['rewrite_mockup', 'insert_in_mockup', 'replace_in_mockup'];
                    if (mockupEditTools.includes(toolCall.tool_name)) {
                        // Determine loading step text based on tool
                        const getMockupLoadingStep = () => {
                            switch (toolCall.tool_name) {
                                case 'rewrite_mockup': return 'Redesigning mockup...';
                                case 'insert_in_mockup': return 'Adding UI elements...';
                                case 'replace_in_mockup': return 'Updating components...';
                                default: return 'Processing...';
                            }
                        };

                        // Set loading state in card immediately
                        setEditedMockupsMap(prev => ({
                            ...prev,
                            [newAiMsgIndex]: {
                                file: { name: toolCall.arguments.file_name },
                                isLoading: true,
                                loadingStep: getMockupLoadingStep()
                            }
                        }));

                        try {
                            // Execute the multi-step edit (reuse document edit endpoint)
                            const result = await editDocumentMutation.mutateAsync({
                                toolName: toolCall.tool_name,
                                fileId: toolCall.arguments.file_id,
                                fileName: toolCall.arguments.file_name,
                                instructions: toolCall.arguments.instructions,
                                signal: controller.signal
                            });

                            if (result.success && result.result) {
                                const editResult = result.result;
                                const editedMockup = {
                                    id: editResult.file_id,
                                    name: editResult.file_name,
                                    category: 'Mockups',
                                    type: 'mockup',
                                    content: editResult.modified_content
                                };

                                // Update the map with full edit info
                                setEditedMockupsMap(prev => ({
                                    ...prev,
                                    [newAiMsgIndex]: {
                                        file: editedMockup,
                                        originalContent: editResult.original_content,
                                        editType: editResult.edit_type,
                                        editSummary: editResult.edit_summary,
                                        isLoading: false
                                    }
                                }));

                                // Open mockup preview panel in diff mode
                                setMockupPanelFile(editedMockup);
                                setMockupOriginalContent(editResult.original_content);
                                setMockupEditSummary(editResult.edit_summary);
                                setIsMockupDiffMode(true);
                                setIsMockupDiffAccepted(false);
                                setActivePanel('mockup');
                                setShowSidebar(false);

                                toast.success(`Mockup updated: ${editResult.file_name}`, {
                                    description: 'Review the changes in the preview panel'
                                });

                                try {
                                    const toolMessage = {
                                        role: 'tool',
                                        content: {
                                            success: true,
                                            tool_name: toolCall.tool_name,
                                            result: {
                                                file_id: editResult.file_id,
                                                file_name: editResult.file_name,
                                                original_content: editResult.original_content,
                                                modified_content: editResult.modified_content,
                                                edit_type: editResult.edit_type,
                                                edit_summary: editResult.edit_summary
                                            }
                                        },
                                        timestamp: new Date().toISOString()
                                    };
                                    setMessages(prev => [...prev, toolMessage]);
                                    await api.post(`/chat-sessions/${currentSessionId}/messages/raw`, toolMessage);
                                } catch (persistError) {
                                    console.error("Failed to persist tool output:", persistError);
                                }
                            }
                        } catch (error) {
                            // Propagate to the outer catch: a stop is not a failure,
                            // and must abandon the remaining tools.
                            if (isAbortError(error)) throw error;
                            console.error('Mockup edit failed:', error);
                            // Update card to show error
                            setEditedMockupsMap(prev => ({
                                ...prev,
                                [newAiMsgIndex]: {
                                    ...prev[newAiMsgIndex],
                                    isLoading: false,
                                    loadingStep: 'Failed to edit mockup'
                                }
                            }));
                            toast.error('Failed to edit mockup', { description: describeError(error).detail });
                        }
                    }

                    // Every branch above is a bare `if` with no `else`, so a tool
                    // the backend declares but this loop has no case for used to
                    // fall straight through: skeleton flipped to `done`, nothing
                    // happened, and the reply went on describing work that was
                    // never attempted. Say so instead.
                    if (!KNOWN_TOOLS.has(toolCall.tool_name)) {
                        console.error('No handler for tool call:', toolCall.tool_name, toolCall.arguments);
                        toast.error("That action isn't supported yet", {
                            description: toolCall.tool_name,
                        });
                    }
                }

                // Loop finished cleanly: every announced tool has produced its
                // real card, so drop the skeletons.
                setPendingToolsByIndex(prev => {
                    const rows = prev[newAiMsgIndex];
                    if (!rows) return prev;
                    return { ...prev, [newAiMsgIndex]: rows.map(t => ({ ...t, status: 'done' })) };
                });
            }
        } catch (error) {
            if (isAbortError(error) || userStoppedRef.current) {
                // Stopping aborts the client's wait, not the server's work: the
                // backend persists both messages once the model returns. So the
                // optimistic user message stays (removing it would desync us)
                // and the turn is marked instead, with a way back to the truth.
                setMessages(prev => (
                    prev[prev.length - 1]?.stopped
                        ? prev
                        : [...prev, { role: 'model', content: '', stopped: true, timestamp: new Date().toISOString() }]
                ));
                // The server almost certainly finished and saved the turn, so
                // refresh the session list to pick up its new title/timestamp.
                sessionsQuery.refetch?.();
                toast.info('Generation stopped');
            } else {
                console.error('Failed to send message:', error);
                const described = describeError(error, 'The message could not be sent.');

                // The failure belongs in the transcript, not in a toast that
                // vanishes and leaves the conversation ending on an unanswered
                // prompt. `failedPrompt` is carried on the marker because a
                // retry cannot trust `messages` — we may never have learned
                // whether the server stored the prompt at all.
                setMessages(prev => [
                    ...trimTrailingMarkers(prev),
                    {
                        role: 'model',
                        content: '',
                        error: described,
                        // The resolved content, not the raw input: quoted editor
                        // selections were folded in during send and their source
                        // state has since been cleared, so replaying the raw
                        // text would silently drop them.
                        failedPrompt: userMsg.content,
                        timestamp: new Date().toISOString(),
                    },
                ]);

                // An expired session redirects to login on its own; anything
                // else is already fully explained by the card, so no toast.
                if (described.kind === 'auth') {
                    toast.error(described.title, { description: described.detail });
                }
            }
        } finally {
            abortRef.current = null;
            setLoading(false);
            setTurnStartedAt(null);
            setActiveTurnContext(null);
            setAnnouncedTools(null);

            // Flip anything left mid-flight to a terminal state so no card
            // spins forever after a stop or a failure.
            if (newAiMsgIndex != null) {
                setPendingToolsByIndex(prev => {
                    const pending = prev[newAiMsgIndex];
                    if (!pending?.length) return prev;
                    const stoppedAll = pending.map(t => (
                        t.status === 'done' ? t : { ...t, status: userStoppedRef.current ? 'cancelled' : 'failed' }
                    ));
                    return { ...prev, [newAiMsgIndex]: stoppedAll };
                });
                const settle = (prev) => (prev[newAiMsgIndex]?.isLoading
                    ? { ...prev, [newAiMsgIndex]: { ...prev[newAiMsgIndex], isLoading: false, loadingStep: 'Stopped' } }
                    : prev);
                setEditedFilesMap(settle);
                setEditedMockupsMap(settle);
            }
            userStoppedRef.current = false;
        }
    };

    const handleStopGeneration = useCallback(() => {
        if (!abortRef.current) return;
        userStoppedRef.current = true;
        abortRef.current.abort();
    }, []);

    // Quick prompts for empty state
    const quickPrompts = [
        { icon: Lightbulb, text: "Help me brainstorm ideas for this project" },
        { icon: FileText, text: "Summarize my project files" },
        { icon: Zap, text: "What should I work on next?" },
        { icon: Wand2, text: "Create a technical specification document" }
    ];

    // ═══════════════════════════════════════════════════════════════════════════
    // TYPEWRITER PLACEHOLDER EXAMPLES
    //
    // Built from the user's own files and tasks, so the @-reference syntax is
    // demonstrated with names they recognise instead of being documented.
    // Each example is [plain, chip, plain] to match the overlay's fixed spans.
    // ═══════════════════════════════════════════════════════════════════════════
    const placeholderExamples = useMemo(() => {
        const out = [];
        const doc = files.find(f => /\.(md|txt|json)$/i.test(f.name || '')) || files[0];
        const task = tasks.find(t => t.status !== 'done') || tasks[0];

        if (doc?.name) {
            out.push([
                { text: 'Summarize ' },
                { text: doc.name, kind: 'file' },
                { text: ' and list the open risks' },
            ]);
        }
        if (task?.title) {
            out.push([
                { text: 'Break ' },
                { text: task.title.slice(0, 40), kind: 'task' },
                { text: ' into subtasks' },
            ]);
        }
        if (files.length > 1) {
            out.push([{ text: 'What should I work on next?' }]);
        }
        out.push([{ text: 'Draft a technical spec for this project' }]);

        return out.slice(0, 4);
    }, [files, tasks]);

    const showTypewriter =
        !readOnly && !input && !loading && !inputFocused && !hasUserMsg(messages);

    const sendQuickPrompt = (text) => {
        setInput('');
        handleSend(null, { text });
    };

    /**
     * Every piece of turn state we hold is keyed by message index, so trimming
     * the array without trimming these would hand the replacement turn the old
     * turn's document cards — they land on exactly the same indices.
     */
    const dropTurnStateFrom = useCallback((fromIndex) => {
        const trim = (prev) => {
            const next = {};
            for (const key of Object.keys(prev)) {
                if (Number(key) < fromIndex) next[key] = prev[key];
            }
            return next;
        };
        setCreatedFilesMap(trim);
        setEditedFilesMap(trim);
        setCreatedMockupsMap(trim);
        setEditedMockupsMap(trim);
        setTurnMetaByIndex(trim);
        setPendingToolsByIndex(trim);
    }, []);

    /** Index of the user message that produced the turn at `index`, or -1. */
    const findPromptIndex = useCallback((index) => {
        for (let i = index - 1; i >= 0; i--) {
            if (messages[i]?.role === 'user') return i;
        }
        return -1;
    }, [messages]);

    /**
     * Re-ask the prompt behind a turn, replacing the old answer rather than
     * appending a second one.
     *
     * The truncate lands *before* any local state changes, so a failure leaves
     * the transcript exactly as it was. Doing it the other way round — trim
     * locally, then call — would show the user a deletion the database never
     * performed, and the next reload would undo it in front of them.
     */
    const retryFromMessage = async (assistantIndex) => {
        if (loading) return;
        // Checked before the truncate, not after: handleSend bails when offline,
        // and a truncate that lands without the resend behind it would delete
        // the old answer with nothing to replace it.
        if (!isOnline) {
            toast.error("You're offline", { description: 'Reconnect to retry this answer.' });
            return;
        }

        const promptIndex = findPromptIndex(assistantIndex);
        if (promptIndex === -1) return;

        const prompt = messages[promptIndex]?.content;
        if (!prompt) return;

        if (currentSessionId) {
            try {
                await truncateMessagesMutation.mutateAsync({
                    sessionId: currentSessionId,
                    fromIndex: promptIndex,
                });
            } catch (error) {
                // Falling back to an append keeps the retry useful, but the
                // user is told the old answer is staying — silently doing
                // something other than what the button says is worse.
                console.error('Truncate before retry failed:', error);
                const described = describeError(error, 'The previous answer could not be removed.');
                toast.error('Retrying as a new turn', { description: described.detail });
                handleSend(null, { text: prompt });
                return;
            }
        }

        setEditingMessageIndex(null);
        setEditingMessageText('');
        dropTurnStateFrom(promptIndex);
        // handleSend does the local trim itself, via `replaceFrom`. Trimming
        // here instead would not help it: it reads the render-scope `messages`
        // to place the new turn, and that array is still the untrimmed one for
        // the rest of this tick.
        handleSend(null, {
            text: prompt,
            replaceFrom: promptIndex,
            preserveAttachments: messages[promptIndex]?.attachments,
            preserveComposer: true,
        });
    };

    /**
     * "Try again" on a failed turn. Same shape as a retry, but the prompt is
     * carried on the marker itself: the send may have failed before we ever
     * learned whether the server stored it, so `messages` is not a reliable
     * source. Truncating is safe either way — it is a no-op when the array is
     * already shorter than the index.
     */
    const retryFailedTurn = async (errorIndex) => {
        if (loading) return;
        if (!isOnline) {
            toast.error("You're offline", { description: 'Reconnect to send this message.' });
            return;
        }

        const marker = messages[errorIndex];
        const promptIndex = findPromptIndex(errorIndex);
        const prompt = marker?.failedPrompt || (promptIndex >= 0 ? messages[promptIndex]?.content : null);
        if (!prompt) return;

        const cutFrom = promptIndex >= 0 ? promptIndex : errorIndex;

        if (currentSessionId) {
            try {
                await truncateMessagesMutation.mutateAsync({
                    sessionId: currentSessionId,
                    fromIndex: cutFrom,
                });
            } catch (error) {
                console.error('Truncate before retry failed:', error);
            }
        }

        dropTurnStateFrom(cutFrom);
        handleSend(null, { text: prompt, replaceFrom: cutFrom });
    };

    /**
     * Recover from a failed *load*, as opposed to a failed send.
     *
     * The sessions-list case can't just call `refetch` and let the init effect
     * sort it out: react-query holds `status: 'error'` across a refetch, so a
     * second failure changes none of that effect's dependencies and it would
     * never re-run — leaving the skeleton up forever. So the outcome is awaited
     * here instead.
     */
    const retrySessionLoad = async () => {
        if (sessionError?.sessionId) {
            loadSession(sessionError.sessionId);
            return;
        }
        setSessionError(null);
        setSessionLoading(true);
        try {
            const res = await sessionsQuery.refetch();
            if (res?.error) throw res.error;
            // Left loading on purpose: the init effect now runs and hands off to
            // loadSession or createNewSession, either of which owns the flag
            // from here. Clearing it would flash the hero in between.
        } catch (err) {
            setSessionError(describeError(err, 'Could not load your conversations.'));
            setSessionLoading(false);
        }
    };

    /**
     * Clear a failed turn without resending.
     *
     * Markers are tail-anchored by `trimTrailingMarkers`, so this only ever
     * removes the last element and no surviving message changes index — which
     * matters, because every result-card map is keyed by index.
     */
    const dismissFailedTurn = useCallback(() => {
        setMessages(prev => trimTrailingMarkers(prev));
    }, []);

    // Drop a user message back into the composer so it can be reworded.
    const editAndResend = (text) => {
        setInput(text);
        requestAnimationFrame(() => inputRef.current?.focusEnd?.());
    };

    const handleStartEditMessage = useCallback((index) => {
        if (loading || isSubmittingEdit) return;
        const msg = messages[index];
        if (!msg || msg.role !== 'user') return;
        setEditingMessageIndex(index);
        setEditingMessageText(msg.content || '');
    }, [loading, isSubmittingEdit, messages]);

    const handleCancelEditMessage = useCallback(() => {
        if (isSubmittingEdit) return;
        setEditingMessageIndex(null);
        setEditingMessageText('');
    }, [isSubmittingEdit]);

    const handleSubmitEditMessage = async () => {
        const targetIndex = editingMessageIndex;
        const newText = editingMessageText;
        if (targetIndex == null || loading || isSubmittingEdit) return;

        const originalMsg = messages[targetIndex];
        if (!originalMsg || originalMsg.role !== 'user') return;

        const trimmedNew = (newText || '').trim();
        const trimmedOriginal = (originalMsg.content || '').trim();
        if (!trimmedNew || trimmedNew === trimmedOriginal) return;

        if (!isOnline) {
            toast.error("You're offline", { description: 'Reconnect to send this edited message.' });
            return;
        }

        setIsSubmittingEdit(true);
        try {
            if (currentSessionId) {
                try {
                    await truncateMessagesMutation.mutateAsync({
                        sessionId: currentSessionId,
                        fromIndex: targetIndex,
                    });
                } catch (error) {
                    console.error('Truncate before edit resend failed:', error);
                    const described = describeError(error, 'The previous message could not be updated.');
                    toast.error('Could not edit message', { description: described.detail });
                    return;
                }
            }

            setEditingMessageIndex(null);
            setEditingMessageText('');
            dropTurnStateFrom(targetIndex);
            await handleSend(null, {
                text: newText,
                replaceFrom: targetIndex,
                preserveAttachments: originalMsg.attachments,
                preserveComposer: true,
            });
        } finally {
            if (isMounted.current) {
                setIsSubmittingEdit(false);
            }
        }
    };

    const handleBranchAndEdit = async (index) => {
        if (loading || isSubmittingEdit || branchSessionMutation.isPending || !currentSessionId) return;
        const targetMsg = messages[index];
        if (!targetMsg || targetMsg.role !== 'user') return;

        if (!isOnline) {
            toast.error("You're offline", { description: 'Reconnect to branch this conversation.' });
            return;
        }

        try {
            // Save current input draft before switching to the branched session
            const currentKey = currentSessionId || 'new';
            setDraftsMap(prev => ({ ...prev, [currentKey]: input }));

            const newSession = await branchSessionMutation.mutateAsync({
                sessionId: currentSessionId,
                fromUserIndex: index,
            });

            if (!isMounted.current) return;

            const branchedMessages = newSession.messages || [];
            staggerCeilingRef.current = 0;
            stickToBottomRef.current = true;
            setShowJumpToLatest(false);
            setTurnMetaByIndex({});
            setPendingToolsByIndex({});
            setCreatedFilesMap({});
            setEditedFilesMap({});
            setCreatedMockupsMap({});
            setEditedMockupsMap({});

            setCurrentSessionId(newSession.id);
            setMessages(branchedMessages);
            setInput('');
            setEditingMessageIndex(index);
            setEditingMessageText(branchedMessages[index]?.content ?? targetMsg.content ?? '');
            setSearchParams({ session: newSession.id }, { replace: true });

            toast.success(`Branched to "${newSession.title}"`);
        } catch (error) {
            console.error('Failed to branch chat session:', error);
            const described = describeError(error, 'Could not branch this conversation.');
            toast.error('Failed to branch conversation', { description: described.detail });
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // TASK PANEL HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    const openTaskPanel = useCallback(({ tab = 'changes', batchId = null } = {}) => {
        setTaskPanelTab(tab);
        setFocusBatchId(batchId);
        setActivePanel('tasks');
        setShowSidebar(false);
    }, []);

    const anyPanelOpen = editorPanelOpen || mockupPanelOpen || taskPanelOpen;

    // What the panel switcher can offer. Booleans first, so the memo's deps are
    // primitives — `agentFile` is replaced on every auto-save keystroke and
    // would otherwise rebuild this list (and re-render the switcher) while you
    // type in the editor.
    const hasEditorArtifact = !!agentFile;
    const hasMockupArtifact = !!mockupPanelFile;
    // Tasks are offered whenever there is anything to look at, agent-made or
    // not: the board tab is a real destination, not just a receipt for a turn.
    const hasTaskArtifact = panelBatches.length > 0 || tasks.length > 0;

    const availablePanels = useMemo(() => {
        const list = [];
        if (hasEditorArtifact) list.push({ id: 'editor', label: 'Doc', title: 'Document', icon: FileText });
        if (hasMockupArtifact) list.push({ id: 'mockup', label: 'Preview', title: 'Mockup preview', icon: MonitorPlay });
        if (hasTaskArtifact) list.push({ id: 'tasks', label: 'Tasks', title: 'Tasks and agent changes', icon: ListChecks });
        return list;
    }, [hasEditorArtifact, hasMockupArtifact, hasTaskArtifact]);

    // One element, handed to all three surfaces. They are mutually exclusive, so
    // only ever one of them mounts it — building it here just keeps the three
    // call sites from drifting apart.
    const panelSwitcher = (
        <ArtifactPanelSwitcher panels={availablePanels} active={activePanel} onSelect={setActivePanel} />
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TRANSCRIPT DERIVED STATE
    // ═══════════════════════════════════════════════════════════════════════════
    const conversationStarted = hasUserMsg(messages);

    // Until the session resolves we do not know whether this chat is empty.
    // Rendering the hero on that guess made every returning conversation flash
    // "How can I help you?" — with quick-prompt buttons — before its own
    // messages arrived, which reads as data loss.
    const resolvingSession = !readOnly && sessionLoading && !sessionError;
    // The shared-link demo has its own resolve step — the script is fetched on
    // demand — and it reuses the same skeleton rather than inventing a second
    // loading screen.
    const resolvingShowcase = readOnly && !showcase;
    const showHero = !conversationStarted && !loading && !readOnly && !resolvingSession && !sessionError;
    const showSkeleton = (resolvingSession && !conversationStarted && !loading) || resolvingShowcase;
    const showTranscript = (conversationStarted || loading || readOnly) && !showSkeleton;

    // One source of truth for the reading column, applied to both the transcript
    // and the composer. They were drifting: the transcript was capped and
    // centred while the composer stretched to whatever the viewport gave it, so
    // on a wide screen the input bar visibly overhung the conversation.
    //
    // No longer narrows when a panel opens: the conversation surface itself is
    // now physically narrower, and swapping the cap re-wrapped and re-measured
    // every message in the transcript the instant a panel appeared.
    const columnClass = 'mx-auto w-full max-w-[64rem] px-4 md:px-6';

    // Messages are filtered while rendering (tool rows and the pre-first-message
    // greeting produce no DOM), so the raw map index would leave gaps in the
    // stagger. Map real index -> visible position instead.
    const visibleIndexById = useMemo(() => {
        const map = new Map();
        let visible = 0;
        messages.forEach((msg, i) => {
            if (msg.role === 'tool') return;
            if (!conversationStarted && !readOnly) return;
            map.set(i, visible++);
        });
        return map;
    }, [messages, conversationStarted, readOnly]);

    const staggerCeiling = staggerCeilingRef.current;

    // On mobile the composer is `fixed`, so it no longer contributes to the
    // scroll container's height. Measure it instead of guessing a magic number
    // that breaks whenever the toolbar or attachment strip changes.
    useEffect(() => {
        const el = inputAreaRef.current;
        if (!el || !isMobile || typeof ResizeObserver === 'undefined') return undefined;
        const write = () => {
            const col = document.querySelector('[data-chat-col]');
            if (col) col.style.setProperty('--chat-input-h', `${el.offsetHeight + 8}px`);
        };
        const ro = new ResizeObserver(write);
        ro.observe(el);
        write();
        return () => ro.disconnect();
    }, [isMobile, showTranscript]);

    /**
     * Say what a turn's task work actually came to.
     *
     * All three outcomes get a voice, because the silent ones are what read as
     * breakage. A turn that changed nothing — every task already in the asked-for
     * state — used to produce no toast, no chip and no panel row, after the UI had
     * already flashed "Applying". "Nothing to do" and "it didn't work" looked
     * identical, and only one of them was true.
     */
    const announceTaskOutcome = useCallback((batches) => {
        const totals = batches.reduce((acc, b) => ({
            created: acc.created + (b.summary?.created || 0),
            updated: acc.updated + (b.summary?.updated || 0),
            deleted: acc.deleted + (b.summary?.deleted || 0),
            skipped: acc.skipped + (b.skipped?.length || 0),
            noop: acc.noop + (b.noop?.length || 0),
        }), { created: 0, updated: 0, deleted: 0, skipped: 0, noop: 0 });

        const applied = totals.created + totals.updated + totals.deleted;
        const view = {
            label: 'View',
            onClick: () => openTaskPanel({ tab: 'changes', batchId: batches[0]?.batch_id }),
        };

        if (applied > 0) {
            const parts = [
                totals.created && `${totals.created} created`,
                totals.updated && `${totals.updated} updated`,
                totals.deleted && `${totals.deleted} deleted`,
            ].filter(Boolean);
            toast.success(parts.join(' · '), {
                description: 'Review, edit or undo in the task panel',
                action: view,
            });
        } else if (totals.noop > 0) {
            toast.info('Already up to date', {
                description: `${totals.noop} task${totals.noop === 1 ? ' was' : 's were'} already in that state.`,
            });
        }

        if (totals.skipped > 0) {
            toast.warning(`${totals.skipped} task${totals.skipped === 1 ? "" : 's'} couldn't be matched`, {
                description: 'The agent referenced a task that no longer exists.',
            });
        }
    }, [openTaskPanel]);

    const handleCloseTaskPanel = useCallback(() => {
        closePanel('tasks');
        setFocusBatchId(null);
        if (sidebarWasOpenRef.current) setShowSidebar(true);
    }, [closePanel]);

    // Same factory as the board and Home: a task typed into this panel is the
    // same task, dated and quadranted the same way, as one typed anywhere else.
    const handlePanelCreateTask = useCallback((title) => {
        const task = buildNewTask(title);
        if (task) createTaskMutation.mutate(task);
    }, [createTaskMutation]);

    const handlePanelUpdateTask = useCallback((taskId, updates) => {
        updateTaskMutation.mutate({ id: taskId, updates: withResolvedDueDate(updates) });
    }, [updateTaskMutation]);

    const handlePanelDeleteTask = useCallback((taskId) => {
        deleteTaskMutation.mutate(taskId);
    }, [deleteTaskMutation]);

    // Track in-flight reverts by id so individual rows can show a spinner without
    // re-rendering the whole changeset on every mutation state change.
    const markReverting = useCallback((id, active) => {
        setRevertingIds(prev => {
            const next = new Set(prev);
            if (active) next.add(id); else next.delete(id);
            return next;
        });
    }, []);

    const handleRevertChange = useCallback(async (change, { force = false } = {}) => {
        markReverting(change.id, true);
        try {
            const data = await revertChangeMutation.mutateAsync({ changeId: change.id, force });
            if (data?.reverted?.length) {
                toast.success(
                    change.op === 'delete' ? 'Task restored' : change.op === 'create' ? 'Task removed' : 'Change reverted',
                    { description: change.task_title }
                );
            }
        } catch {
            /* surfaced by the mutation's onError */
        } finally {
            markReverting(change.id, false);
        }
    }, [markReverting, revertChangeMutation]);

    const handleRevertBatch = useCallback(async (batch, { force = false } = {}) => {
        markReverting(batch.batch_id, true);
        try {
            const data = await revertBatchMutation.mutateAsync({ batchId: batch.batch_id, force });
            const count = data?.reverted?.length || 0;
            if (count) {
                toast.success(`Reverted ${count} change${count === 1 ? '' : 's'}`, {
                    description: 'The agent will be told on your next message.',
                });
            }
        } catch {
            /* surfaced by the mutation's onError */
        } finally {
            markReverting(batch.batch_id, false);
        }
    }, [markReverting, revertBatchMutation]);

    // Escape stops a running generation. Registered in the capture phase and
    // ahead of the task-panel handler above, and deliberately NOT skipped while
    // focus is in the composer — that is exactly where the user's hands are.
    useEffect(() => {
        if (!loading) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            handleStopGeneration();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [loading, handleStopGeneration]);

    // ═══════════════════════════════════════════════════════════════════════════
    // AGENTIC EDITOR HANDLERS - Auto-save and seamless editing
    // ═══════════════════════════════════════════════════════════════════════════

    // NOTE: handleAgentFileContentChange is defined above before the conditional return

    // Open file in full editor (expands the panel in chat)
    const handleOpenInFullEditor = () => {
        // Just ensure the panel is open - user can view/edit in the expanded panel
        if (agentFile) {
            setActivePanel('editor');
            setShowSidebar(false);
        }
    };

    // Close editor panel (normal mode)
    const handleCloseEditorPanel = () => {
        closePanel('editor');
        setAgentFile(null);
        setIsDiffMode(false);
        setOriginalContent('');
        setEditSummary('');
        setIsDiffAccepted(false);
        if (sidebarWasOpenRef.current) {
            setShowSidebar(true);
        }
    };

    // Accept changes from diff view - save to backend and switch to normal mode
    const handleAcceptChanges = async () => {
        if (!agentFile?.id || !agentFile?.content) return;

        // Store file reference before closing
        const fileToKeepOpen = { ...agentFile };

        try {
            await acceptChangesMutation.mutateAsync({
                fileId: agentFile.id,
                newContent: agentFile.content
            });

            // IMPORTANT: To avoid Monaco DiffEditor disposal errors,
            // we close the panel first, wait for unmount, then reopen in normal mode
            closePanel('editor');
            setIsDiffMode(false);
            setOriginalContent('');

            // Reopen panel in normal mode after a brief delay (allows DiffEditor to fully unmount)
            setTimeout(() => {
                setAgentFile(fileToKeepOpen);
                setActivePanel('editor');
            }, 100);
        } catch (error) {
            console.error('Failed to accept changes:', error);
        }
    };

    // Reject changes - close diff view without saving
    const handleRejectChanges = () => {
        toast.info('Changes discarded');
        handleCloseEditorPanel();
    };

    // ═══════════════════════════════════════════════════════════════════════════════
    // MOCKUP PANEL HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════════

    // Close mockup panel
    const handleCloseMockupPanel = () => {
        closePanel('mockup');
        setMockupPanelFile(null);
        setIsMockupDiffMode(false);
        setMockupOriginalContent('');
        setMockupEditSummary('');
        setIsMockupDiffAccepted(false);
        if (sidebarWasOpenRef.current) {
            setShowSidebar(true);
        }
    };

    // Escape closes whichever artifact panel is open. This used to live inside
    // the task panel's resize effect, so only that one panel responded to it.
    //
    // Declared here, below all three close handlers, so they're initialised
    // before the dependency array reads them.
    //
    // Bails while a generation is running: the capture-phase handler above owns
    // Escape then, and stopping the run must win over closing a panel.
    useEffect(() => {
        if (!anyPanelOpen || loading) return undefined;

        const handleKey = (e) => {
            // Don't steal Escape from an inline editor or a field picker.
            if (e.key !== 'Escape') return;
            const el = document.activeElement;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
            if (taskPanelOpen) handleCloseTaskPanel();
            else if (editorPanelOpen) handleCloseEditorPanel();
            else if (mockupPanelOpen) handleCloseMockupPanel();
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
        // handleClose{Editor,Mockup}Panel are re-created each render; the panel
        // flags below already re-run this effect whenever they'd matter.
        // eslint-disable-next-line
    }, [anyPanelOpen, loading, taskPanelOpen, editorPanelOpen, mockupPanelOpen, handleCloseTaskPanel]);

    // Accept mockup changes - save to backend
    const handleAcceptMockupChanges = async () => {
        if (!mockupPanelFile?.id || !mockupPanelFile?.content) return;

        const fileToKeepOpen = { ...mockupPanelFile };

        try {
            await acceptChangesMutation.mutateAsync({
                fileId: mockupPanelFile.id,
                newContent: mockupPanelFile.content
            });

            // Close and reopen to avoid Monaco errors
            closePanel('mockup');
            setIsMockupDiffMode(false);
            setMockupOriginalContent('');

            setTimeout(() => {
                setMockupPanelFile(fileToKeepOpen);
                setActivePanel('mockup');
            }, 100);

            toast.success('Mockup changes applied!');
        } catch (error) {
            console.error('Failed to accept mockup changes:', error);
            toast.error('Failed to save changes');
        }
    };

    // Reject mockup changes
    const handleRejectMockupChanges = () => {
        toast.info('Mockup changes discarded');
        handleCloseMockupPanel();
    };

    // Mockup content change handler
    // Mockup content change handler
    const handleMockupContentChange = useCallback(
        debounce(async (fileId, newContent) => {
            if (!fileId || !newContent) return;

            // Allow local editing even if it's a history file, but don't save to DB
            if (fileId === 'history') {
                setMockupPanelFile(prev => prev?.id === fileId ? { ...prev, content: newContent } : prev);
                return;
            }

            try {
                await updateFileMutation.mutateAsync({
                    id: fileId,
                    updates: { content: newContent }
                });
                setMockupPanelFile(prev => prev?.id === fileId ? { ...prev, content: newContent } : prev);
            } catch (error) {
                console.error('Auto-save mockup failed:', error);
            }
        }, 1000),
        [updateFileMutation]
    );

    // Open mockup in full editor (expands the panel in chat)
    const handleOpenMockupInFullEditor = () => {
        // Just ensure the panel is open - user can view/edit in the expanded panel
        if (mockupPanelFile) {
            setActivePanel('mockup');
            setShowSidebar(false);
        }
    };

    // Input Handler for @ Mentions & Drafts
    const handleInputChange = (e) => {
        const val = e.target.value;
        setInput(val);
        const activeKey = currentSessionId || 'new';
        setDraftsMap(prev => ({ ...prev, [activeKey]: val }));

        const lastChar = val.slice(-1);
        if (lastChar === '@') {
            setShowMentionPicker(true);
            setMentionSearch('');
        } else if (showMentionPicker) {
            const match = val.match(/@([\w\s\.-]*)$/);
            if (match) {
                setMentionSearch(match[1]);
            } else {
                setShowMentionPicker(false);
            }
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════════
    // FILE UPLOAD & DRAG-DROP HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════════

    // Helper: Detect file type from extension
    const getFileType = (filename) => {
        const ext = filename.split('.').pop().toLowerCase();
        const codeExts = ['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'go', 'rs', 'rb', 'php', 'swift', 'kt'];
        const docExts = ['md', 'txt', 'json', 'yaml', 'yml', 'xml', 'html', 'css', 'scss', 'less'];
        const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
        if (imageExts.includes(ext)) return 'image';
        if (codeExts.includes(ext)) return 'code';
        if (docExts.includes(ext)) return 'doc';
        return 'doc';
    };

    // Helper: Check if file is an image
    const isImageFile = (file) => {
        return file.type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(file.name);
    };

    // Process dropped/selected files (text + images)
    const processFiles = async (fileList) => {
        const newFiles = [];
        for (const file of fileList) {
            // Handle image files
            if (isImageFile(file)) {
                if (file.size > 20 * 1024 * 1024) {
                    toast.error(`${file.name} is too large (max 20MB for images)`);
                    continue;
                }

                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });

                newFiles.push({
                    id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    name: file.name,
                    type: 'image',
                    mimeType: file.type || 'image/png',
                    dataUrl, // base64 data URL for preview and sending
                    size: file.size
                });
                continue;
            }

            // Handle text files
            if (!file.type.startsWith('text/') && !file.name.match(/\.(md|txt|json|js|jsx|ts|tsx|py|java|c|cpp|go|rs|yaml|yml|xml|html|css|scss)$/i)) {
                toast.error(`Skipped ${file.name}: Unsupported file type`);
                continue;
            }

            const content = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsText(file);
            });

            newFiles.push({
                id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                name: file.name,
                content,
                type: getFileType(file.name),
                size: file.size
            });
        }

        if (newFiles.length > 0) {
            setUploadedFiles(prev => [...prev, ...newFiles]);
            toast.success(`${newFiles.length} file${newFiles.length > 1 ? 's' : ''} ready to send`);
        }
    };

    // Handle paste event for clipboard images
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        // Check for images first
        const imageItems = [];
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file) {
                    imageItems.push(file);
                }
            }
        }

        // If we found images, prevent default IMMEDIATELY and process them
        if (imageItems.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            processFiles(imageItems); // Fire and forget - no await needed
        }
    };

    // Drag-drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) processFiles(files);
    };

    // File input change handler
    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) processFiles(files);
        e.target.value = '';
    };

    // Remove uploaded file
    const removeUploadedFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const selectItem = (item) => {
        const name = item.type === 'file' ? item.name : item.title;
        const tagType = item.type === 'file' ? 'File' : 'Task';
        const tag = `@[${tagType}: ${name}]`;
        const newVal = input.replace(/@[\w\s\.-]*$/, tag + ' ');
        setInput(newVal);
        setReferencedItems(prev => [...prev, item]);
        setShowMentionPicker(false);
        setTimeout(() => {
            inputRef.current?.focusEnd?.();
        }, 0);
    };

    // Handle adding selected text from editor to chat
    const handleAddToChat = useCallback(({ file, text, range }) => {
        console.log('[Chat] handleAddToChat called', { file, text });
        if (!text) return;

        // Create the tag to insert
        const locationRef = range ? `:${range.startLineNumber}-${range.endLineNumber}` : '';
        // Add spaces around for safety, but check if we are at start
        const tag = ` @[File: ${file.name}${locationRef}] `;

        // For contentEditable, selectionStart is unreliable.
        // We simply append to the end for consistent behavior.
        const newInput = input + tag;

        setInput(newInput);

        // Store data for resolution on send
        setAttachedSelections(prev => [
            ...prev,
            { id: Date.now(), file, text, range }
        ]);

        // Focus and set cursor to end
        setTimeout(() => {
            if (inputRef.current && inputRef.current.focusEnd) {
                inputRef.current.focusEnd();
            } else if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 10); // Slight delay to ensure render
    }, [input]);

    // Filtered candidates for the @ mention picker (files + tasks)
    const mentionCandidates = useMemo(() => {
        const q = (mentionSearch || '').toLowerCase();
        const fileItems = files
            .filter(f => (f.name || '').toLowerCase().includes(q))
            .map(f => ({ ...f, type: 'file' }));
        const taskItems = tasks
            .filter(t => (t.title || '').toLowerCase().includes(q))
            .map(t => ({ ...t, type: 'task' }));
        return [...fileItems, ...taskItems];
    }, [files, tasks, mentionSearch]);

    useEffect(() => {
        setMentionSelectedIndex(0);
    }, [mentionSearch, showMentionPicker]);

    // All files & tasks currently active in this conversation's context (selective mode)
    const activeContextItems = useMemo(() => {
        const refs = collectConversationReferences(messages, input, referencedItems, files, tasks);
        const items = [];
        for (const name of refs.fileNames) {
            items.push({ type: 'file', label: name });
        }
        for (const title of refs.taskTitles) {
            items.push({ type: 'task', label: title });
        }
        return items;
    }, [messages, input, referencedItems, files, tasks]);

    const activeSession = useMemo(
        () => sessions.find(s => s.id === currentSessionId) || null,
        [sessions, currentSessionId]
    );

    const isSessionBranched = (session) =>
        Boolean(session?.branched_from_session_id || /^branch of\s*:/i.test(session?.title || ''));

    const handleStartRename = (session, e, source = 'sidebar') => {
        if (!session?.id) return;
        e?.stopPropagation();
        setEditingSessionId(session.id);
        setEditingTitle(session.title || '');
        setEditingSessionSource(source);
    };

    const handleCancelRename = (e) => {
        e?.stopPropagation();
        setEditingSessionId(null);
        setEditingSessionSource(null);
    };

    const handleSaveRename = async (sessionId, e) => {
        e?.stopPropagation();
        const trimmed = (editingTitle || '').trim();
        const originalTitle = (sessions.find(s => s.id === sessionId)?.title || '').trim();
        if (!trimmed || trimmed === originalTitle) {
            setEditingSessionId(null);
            setEditingSessionSource(null);
            return;
        }
        try {
            await updateSessionMutation.mutateAsync({ sessionId, updates: { title: trimmed } });
            toast.success("Chat renamed");
        } catch (err) {
            toast.error("Failed to rename chat");
        } finally {
            setEditingSessionId(null);
            setEditingSessionSource(null);
        }
    };

    const handleRenameKeyDown = (sessionId, e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (e.nativeEvent?.isComposing || e.isComposing || e.keyCode === 229) return;
            handleSaveRename(sessionId, e);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            handleCancelRename(e);
        }
    };

    // Renders the sessions sidebar. Deliberately invoked as `renderSessionSidebar()`
    // rather than as a JSX element: as an element it got a fresh
    // component identity on every ProjectChat render, which unmounted the whole
    // subtree — including the autoFocus rename input, which then re-stole focus
    // on every keystroke. It uses no hooks, so calling it inlines safely.
    const renderSessionSidebar = () => {
        return (
            <div className="h-full flex flex-col bg-transparent">
                {/* Sidebar Header */}
                <div className="flex items-center justify-between px-4 border-b border-white/5 h-12 flex-shrink-0">
                    <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-muted-foreground/70">
                        Conversations
                    </h3>
                    {/* Themed off the tokens, not a hardcoded palette. It used
                        to be `bg-indigo-600` with `text-white` and an
                        `indigo-600/30` shadow — close enough to `--primary`
                        (239 84% 67%) to look like a mistake rather than a
                        choice, visibly darker than every other primary surface,
                        and stuck on white text no matter what the theme sets. */}
                    <Button
                        size="sm"
                        className="flex h-7 cursor-pointer items-center gap-1 rounded-lg border-0 bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-md shadow-primary/25 transition-all hover:bg-primary/90 active:scale-95"
                        onClick={() => {
                            createNewSession();
                        }}
                    >
                        <Plus className="h-3.5 w-3.5 stroke-[2.5]" />
                        <span>New</span>
                    </Button>
                </div>

                {/* Sessions List */}
                <ScrollArea className="flex-1 px-3 py-2">
                    <div className="space-y-1 pb-4">
                        {sessions.map(session => {
                            const isEditingInSidebar =
                                editingSessionId === session.id && editingSessionSource !== 'header';
                            const branched = isSessionBranched(session);

                            return (
                            <div
                                key={session.id}
                                onClick={() => {
                                    if (editingSessionId === session.id) return;
                                    if (session.id === currentSessionId && !sessionError) return;
                                    loadSession(session.id);
                                }}
                                onDoubleClick={(e) => handleStartRename(session, e, 'sidebar')}
                                className={`
                                    group relative flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all duration-200
                                    ${session.id === currentSessionId
                                        ? 'bg-primary/15 border border-primary/30 text-foreground shadow-sm shadow-primary/5 font-medium'
                                        : 'hover:bg-white/5 border border-transparent text-muted-foreground hover:text-foreground'}
                                `}
                            >
                                {/* Icon */}
                                <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                    session.id === currentSessionId ? 'bg-primary/25 text-primary' : 'bg-secondary/60 text-muted-foreground'
                                }`}>
                                    {session.pinned ? (
                                        <Pin className="h-4 w-4 text-accent fill-accent" />
                                    ) : branched ? (
                                        <GitBranch className="h-4 w-4" />
                                    ) : (
                                        <MessageSquare className="h-4 w-4" />
                                    )}
                                </div>

                                {/* Title / Inline Rename Input */}
                                <div className="flex-1 min-w-0 grid gap-0.5">
                                    {isEditingInSidebar ? (
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="text"
                                                aria-label="Rename conversation"
                                                value={editingTitle}
                                                onChange={(e) => setEditingTitle(e.target.value)}
                                                onKeyDown={(e) => handleRenameKeyDown(session.id, e)}
                                                onBlur={(e) => handleSaveRename(session.id, e)}
                                                onFocus={(e) => e.target.select()}
                                                autoFocus
                                                data-testid="sidebar-rename-input"
                                                className="w-full bg-black/80 border border-primary/50 rounded-md px-2 py-0.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                                            />
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); handleSaveRename(session.id, e); }}
                                                className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400"
                                                title="Save title"
                                            >
                                                <Check className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); handleCancelRename(e); }}
                                                className="p-1 rounded hover:bg-white/10 text-muted-foreground"
                                                title="Cancel"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <p
                                                onDoubleClick={(e) => handleStartRename(session, e, 'sidebar')}
                                                title="Double-click to rename"
                                                className="text-sm font-medium truncate pr-1 select-none"
                                            >
                                                {session.title}
                                            </p>
                                            <p className="text-[10px] text-muted-foreground/70 truncate font-mono select-none">
                                                {formatDistanceToNow(new Date(session.updated_at))} ago
                                            </p>
                                        </>
                                    )}
                                </div>

                                {/* Actions */}
                                {!isEditingInSidebar && (
                                    <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => handleStartRename(session, e, 'sidebar')}
                                            className="p-1 rounded-md hover:bg-white/10 text-muted-foreground hover:text-foreground transition-all"
                                            title="Rename chat"
                                        >
                                            <Pencil className="h-3 w-3" />
                                        </button>
                                        <button
                                            onClick={(e) => togglePin(session.id, session.pinned, e)}
                                            className={`p-1 rounded-md transition-all ${
                                                session.pinned ? 'text-accent' : 'hover:bg-white/10 text-muted-foreground hover:text-foreground'
                                            }`}
                                            title={session.pinned ? 'Unpin' : 'Pin'}
                                        >
                                            <Pin className={`h-3 w-3 ${session.pinned ? 'fill-current' : ''}`} />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmSession(session.id); }}
                                            className="p-1 rounded-md hover:bg-red-500/20 hover:text-red-400 text-muted-foreground transition-all"
                                            title="Delete chat"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                        })}
                    </div>
                </ScrollArea>
            </div>
        );
    };

    return (
        <>
        {/* The conversation surface. The session list stays inside it as a
            second pane rather than becoming a third floating box — it's a way
            of navigating this surface, not an artifact in its own right. */}
        <Surface aria-label="Conversation" className="flex-1 flex-row">

            {/* Desktop Sidebar */}
            {!isMobile && !readOnly && (
                <div className={`
                    ${showSidebar ? 'w-72' : 'w-0'} 
                    flex-shrink-0 border-r border-white/5 bg-transparent
                    transition-all duration-300 overflow-hidden
                `}>
                    <div className="h-full w-72">
                        {renderSessionSidebar()}
                    </div>
                </div>
            )}


            {/* Main Chat Area - Now a horizontal flex container */}
            <div className="flex-1 flex min-w-0 relative">

                {/* Chat Content Section - Shrinks when an artifact panel opens.
                    No margin (the Layout row owns the gap) and no width
                    transition: this shrink is the same layout pass the panel's
                    appearance triggers, and it has to land in one frame or
                    Monaco/Sandpack re-measure on every frame of it. */}
                <div
                    className="relative flex min-w-0 flex-1 flex-col"
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    {/* Shared-link demo bar. Takes the header slot the session
                        title occupies for signed-in users — that row is empty
                        on a shared link, and the framing has to be permanently
                        on screen, not scrolled past once. */}
                    {readOnly ? (
                        <ShowcaseDemoBar
                            phase={showcasePlayback.phase}
                            onSkip={showcasePlayback.skip}
                            onReplay={showcasePlayback.replay}
                        />
                    ) : null}

                    {/* Desktop Header Bar */}
                    {!isMobile && !readOnly && (
                        <div className="flex flex-shrink-0 items-center justify-between px-4 py-2 border-b border-white/5 bg-transparent z-40 h-12">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowSidebar(prev => !prev)}
                                    className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-white/10 rounded-lg transition-all"
                                    title={showSidebar ? "Collapse sidebar" : "Expand sidebar"}
                                >
                                    <PanelLeft className="h-4 w-4" />
                                </Button>
                                <div className="flex items-center gap-2 min-w-0 flex-1 max-w-md">
                                    {editingSessionId === currentSessionId && editingSessionSource === 'header' && activeSession ? (
                                        <div className="flex items-center gap-1.5 w-full max-w-xs" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="text"
                                                aria-label="Rename conversation"
                                                value={editingTitle}
                                                onChange={(e) => setEditingTitle(e.target.value)}
                                                onKeyDown={(e) => handleRenameKeyDown(activeSession.id, e)}
                                                onBlur={(e) => handleSaveRename(activeSession.id, e)}
                                                onFocus={(e) => e.target.select()}
                                                autoFocus
                                                data-testid="header-rename-input"
                                                className="w-full bg-black/80 border border-primary/50 rounded-md px-2.5 py-1 text-xs font-mono font-bold tracking-wider text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                                            />
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); handleSaveRename(activeSession.id, e); }}
                                                className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400 flex-shrink-0"
                                                title="Save title"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); handleCancelRename(e); }}
                                                className="p-1 rounded hover:bg-white/10 text-muted-foreground flex-shrink-0"
                                                title="Cancel"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            onDoubleClick={(e) => activeSession && handleStartRename(activeSession, e, 'header')}
                                            title={activeSession ? "Double-click to rename conversation" : undefined}
                                            data-testid="header-session-title"
                                            className={cn(
                                                "group flex items-center gap-1.5 min-w-0 rounded-md px-1.5 py-1 -ml-1.5 transition-colors select-none",
                                                activeSession ? "cursor-pointer hover:bg-white/5" : ""
                                            )}
                                        >
                                            {isSessionBranched(activeSession) && (
                                                <GitBranch className="h-3.5 w-3.5 text-primary/80 flex-shrink-0" />
                                            )}
                                            <span className="text-xs font-mono font-bold uppercase tracking-wider text-muted-foreground/80 group-hover:text-foreground transition-colors truncate">
                                                {activeSession?.title || "New Chat"}
                                            </span>
                                            {activeSession && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleStartRename(activeSession, e, 'header')}
                                                    className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10 text-muted-foreground hover:text-foreground transition-all flex-shrink-0"
                                                    title="Rename conversation"
                                                >
                                                    <Pencil className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Drag Overlay */}
                    {isDragging && (
                        <div className="absolute inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center rounded-2xl border-2 border-dashed border-primary/50 transition-all animate-in fade-in-0 duration-200">
                            <div className="flex flex-col items-center gap-4 p-8 text-center">
                                <div className="h-16 w-16 rounded-2xl bg-primary/20 flex items-center justify-center border border-primary/30 animate-bounce">
                                    <Upload className="h-8 w-8 text-primary" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-primary">Drop files here</h3>
                                    <p className="text-sm text-muted-foreground mt-1">Supported: .md, .txt, .js, .py, .json, etc.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Mobile Header */}
                    {isMobile && !readOnly && (
                        <div className="flex flex-shrink-0 items-center gap-3 px-4 py-3 border-b border-white/5 bg-background/60 backdrop-blur-xl z-40 h-16">
                            <Sheet>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-9 w-9 -ml-2 text-muted-foreground">
                                        <Menu className="h-5 w-5" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="w-[85%] max-w-[320px] p-0 border-r border-white/10 bg-black/90 backdrop-blur-2xl">
                                    {renderSessionSidebar()}
                                </SheetContent>
                            </Sheet>
                            <div className="flex-1 min-w-0">
                                {editingSessionId === currentSessionId && editingSessionSource === 'header' && activeSession ? (
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="text"
                                            aria-label="Rename conversation"
                                            value={editingTitle}
                                            onChange={(e) => setEditingTitle(e.target.value)}
                                            onKeyDown={(e) => handleRenameKeyDown(activeSession.id, e)}
                                            onBlur={(e) => handleSaveRename(activeSession.id, e)}
                                            onFocus={(e) => e.target.select()}
                                            autoFocus
                                            className="w-full bg-black/80 border border-primary/50 rounded-md px-2 py-0.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                                        />
                                        <button
                                            type="button"
                                            onMouseDown={(e) => { e.preventDefault(); handleSaveRename(activeSession.id, e); }}
                                            className="p-1 rounded hover:bg-emerald-500/20 text-emerald-400"
                                            title="Save title"
                                        >
                                            <Check className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onMouseDown={(e) => { e.preventDefault(); handleCancelRename(e); }}
                                            className="p-1 rounded hover:bg-white/10 text-muted-foreground"
                                            title="Cancel"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <h2
                                        onDoubleClick={(e) => activeSession && handleStartRename(activeSession, e, 'header')}
                                        title={activeSession ? "Double-click to rename" : undefined}
                                        className="text-sm font-semibold truncate leading-tight select-none cursor-pointer"
                                    >
                                        {activeSession?.title || "New Chat"}
                                    </h2>
                                )}
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <div className={`h-1.5 w-1.5 rounded-full ${isMobile ? 'bg-green-500' : 'bg-primary'}`}></div>
                                    Forge AI
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════
                        TRANSCRIPT
                        The scroll container is `flex-1` inside the column, so its
                        height already equals viewport − header − composer. Giving
                        it a flex context lets the hero centre with `m-auto`, which
                        (unlike a min-height calc) stays correct as the composer
                        grows and degrades to scrolling when content overflows.
                    ═══════════════════════════════════════════════════════════ */}
                    <div
                        ref={scrollRef}
                        onScroll={handleTranscriptScroll}
                        onWheel={handleTranscriptWheel}
                        onTouchMove={handleTranscriptTouchMove}
                        className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
                    >
                        <div
                            data-chat-col
                            className={cn('flex flex-1 flex-col', columnClass)}
                            style={isMobile ? { paddingBottom: 'var(--chat-input-h, 12rem)' } : undefined}
                        >
                            {/* One-cell grid stack: hero, skeleton and transcript
                                share the same box, so each fades in over the one
                                leaving with no reflow snap. */}
                            <div className="grid grid-cols-1 flex-1 [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:min-w-0">
                                <AnimatePresence initial={false}>
                                    {showSkeleton && <ChatTranscriptSkeleton key="skeleton" reduceMotion={reduceMotion} />}
                                </AnimatePresence>

                                {/* The session itself failed to load. Shown in
                                    place of the transcript with a retry that
                                    re-runs exactly the load that failed. */}
                                {sessionError && !conversationStarted && (
                                    <div className="m-auto w-full max-w-xl py-8">
                                        <ChatErrorTurn error={sessionError} onRetry={retrySessionLoad} />
                                    </div>
                                )}

                                <AnimatePresence initial={false}>
                                    {showHero && (
                                        <motion.div
                                            key="hero"
                                            className="m-auto w-full max-w-xl py-8"
                                            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -16 }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <ChatEmptyState
                                                quickPrompts={quickPrompts}
                                                onPick={sendQuickPrompt}
                                                reduceMotion={reduceMotion}
                                            />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {showTranscript && (
                                    <div ref={transcriptRef} className="flex w-full flex-col gap-8 self-start pb-24 pt-8">
                                {/* Read-only mode: the demo's opening frame. It
                                    is the first thing in the transcript and it
                                    is on screen before the first message lands,
                                    so nobody starts reading mid-conversation
                                    without knowing what they're looking at. */}
                                {readOnly ? <ShowcaseIntroCard reduceMotion={reduceMotion} /> : null}

                                {/* Messages */}
                                {messages.map((msg, i) => {
                                    // Skip persisted tool outputs (they are for rehydration only)
                                    if (msg.role === 'tool') return null;

                                    // Hide initial model greeting when user hasn't sent any messages yet (except in readOnly demo mode)
                                    if (!hasUserMsg(messages) && !readOnly) return null;

                                    const editedFileWithStatus = editedFilesWithStatus[i] || null;
                                    const editedMockupWithStatus = editedMockupsWithStatus[i] || null;

                                    // Task work leaves only a summary chip in the stream;
                                    // the detail (and every control) lives in the panel.
                                    // The chip only ever renders a settled changeset now —
                                    // the writes land before the reply does, so there is no
                                    // in-between state left for it to show.
                                    const taskChanges = taskChipsByIndex.get(i);

                                    // Stagger only when a whole conversation loads at
                                    // once. A delay on the reply you just waited for
                                    // reads as lag, not polish.
                                    const visibleIndex = visibleIndexById.get(i) ?? 0;
                                    const entranceDelay = visibleIndex < staggerCeiling
                                        ? Math.min(visibleIndex * 0.05, 0.3)
                                        : 0;

                                    return (
                                        <ChatMessage
                                            key={i}
                                            message={msg}
                                            messageIndex={i}
                                            files={files}
                                            tasks={tasks}
                                            navigate={navigate}
                                            readOnly={readOnly}
                                            baseUrl={baseUrl}
                                            reduceMotion={reduceMotion}
                                            entranceDelay={entranceDelay}
                                            turnMeta={turnMetaByIndex[i]}
                                            pendingTools={pendingToolsByIndex[i]}
                                            onRetry={
                                                readOnly ? undefined
                                                    : msg.error ? () => retryFailedTurn(i)
                                                        : msg.role === 'model' && !msg.stopped ? () => retryFromMessage(i)
                                                            : undefined
                                            }
                                            isLastUserMessage={msg.role === 'user' && i === lastUserMessageIndex}
                                            isEditing={msg.role === 'user' && editingMessageIndex === i}
                                            editingText={editingMessageIndex === i ? editingMessageText : ''}
                                            onEditChange={setEditingMessageText}
                                            onStartEdit={
                                                msg.role === 'user' && !readOnly && i === lastUserMessageIndex
                                                    ? () => handleStartEditMessage(i)
                                                    : undefined
                                            }
                                            onCancelEdit={handleCancelEditMessage}
                                            onSubmitEdit={handleSubmitEditMessage}
                                            onBranchAndEdit={
                                                msg.role === 'user' && !readOnly && i !== lastUserMessageIndex
                                                    ? () => handleBranchAndEdit(i)
                                                    : undefined
                                            }
                                            isSubmittingEdit={isSubmittingEdit || loading || branchSessionMutation.isPending}
                                            onEditAndResend={msg.role === 'user' && !readOnly ? () => editAndResend(msg.content) : undefined}
                                            onDismissError={msg.error && !readOnly ? dismissFailedTurn : undefined}
                                            onReload={(msg.stopped || msg.error) ? () => loadSession(currentSessionId) : undefined}
                                            createdFile={createdFilesMap[i]}
                                            editedFile={editedFileWithStatus}
                                            createdMockup={createdMockupsMap[i]}
                                            editedMockup={editedMockupWithStatus}
                                            taskChanges={taskChanges}
                                            onOpenTaskChanges={() => openTaskPanel({
                                                tab: 'changes',
                                                batchId: taskChanges?.batch_id || null,
                                            })}
                                            onOpenAgentFile={() => {
                                                const fileForMessage = createdFilesMap[i];
                                                if (fileForMessage) {
                                                    setAgentFile(fileForMessage);
                                                    setIsDiffMode(false);
                                                    setActivePanel('editor');
                                                    setShowSidebar(false);
                                                }
                                            }}
                                            onOpenInFullEditor={() => {
                                                // Open in expanded panel instead of navigating away
                                                const fileForMessage = createdFilesMap[i];
                                                if (fileForMessage) {
                                                    const latest = files.find(f => f.id === fileForMessage.id) || fileForMessage;
                                                    setAgentFile(latest);
                                                    setIsDiffMode(false);
                                                    setActivePanel('editor');
                                                    setShowSidebar(false);
                                                }
                                            }}
                                            onViewDiff={editedFileWithStatus?.originalContent ? () => {
                                                const edited = editedFileWithStatus;
                                                if (edited) {
                                                    // Get latest file content from context for accurate diff view
                                                    const latestFile = files.find(f => f.id === edited.file.id);
                                                    const fileToShow = latestFile ? {
                                                        ...edited.file,
                                                        content: latestFile.content // Use latest content
                                                    } : {
                                                        ...edited.file,
                                                        content: edited.modifiedContent || edited.diffData?.modified_content
                                                    };

                                                    setAgentFile(fileToShow);
                                                    setOriginalContent(edited.originalContent);
                                                    setEditSummary(edited.editSummary);
                                                    setIsDiffAccepted(edited.isAccepted || false); // Track if already accepted
                                                    setIsDiffMode(true);
                                                    setActivePanel('editor');
                                                    setShowSidebar(false);
                                                }
                                            } : undefined}
                                            onOpenEditedInFullEditor={() => {
                                                const edited = editedFileWithStatus;
                                                if (edited?.file?.id) {
                                                    const latestFile = files.find(f => f.id === edited.file.id) || edited.file;
                                                    setAgentFile(latestFile);
                                                    setIsDiffMode(false);
                                                    setActivePanel('editor');
                                                    setShowSidebar(false);
                                                }
                                            }}
                                            // Mockup handlers
                                            onOpenMockupPreview={() => {
                                                const mockupForMessage = createdMockupsMap[i];
                                                if (mockupForMessage) {
                                                    setMockupPanelFile(mockupForMessage);
                                                    setIsMockupDiffMode(false);
                                                    setActivePanel('mockup');
                                                    setShowSidebar(false);
                                                }
                                            }}
                                            onOpenMockupInEditor={() => {
                                                // Open in expanded panel instead of navigating away
                                                const mockupForMessage = createdMockupsMap[i];
                                                if (mockupForMessage) {
                                                    const latest = files.find(f => f.id === mockupForMessage.id) || mockupForMessage;
                                                    setMockupPanelFile(latest);
                                                    setIsMockupDiffMode(false);
                                                    setActivePanel('mockup');
                                                    setShowSidebar(false);
                                                }
                                            }}
                                            onViewMockupDiff={editedMockupsMap[i]?.originalContent ? () => {
                                                const edited = editedMockupsMap[i];
                                                if (edited) {
                                                    // Try to resolve content safely
                                                    const latestFile = files.find(f => f.name === edited.file.name);

                                                    // For diff view, we need BOTH original and modified.
                                                    // If we have latestFile, we can show its content as 'modified' (current state).
                                                    // If we don't, we need edited.modifiedContent.

                                                    const modifiedContent = latestFile ? latestFile.content : edited.modifiedContent;

                                                    if (modifiedContent) {
                                                        const mockupToShow = {
                                                            ...edited.file,
                                                            id: latestFile?.id || edited.file.id, // Prefer real ID
                                                            content: modifiedContent
                                                        };

                                                        setMockupPanelFile(mockupToShow);
                                                        setMockupOriginalContent(edited.originalContent);
                                                        setMockupEditSummary(edited.editSummary);
                                                        setIsMockupDiffAccepted(edited.isAccepted || false);
                                                        setIsMockupDiffMode(true);
                                                        setActivePanel('mockup');
                                                        setShowSidebar(false);
                                                    } else {
                                                        toast.error("Diff unavailable", {
                                                            description: "The content for this version is missing."
                                                        });
                                                    }
                                                }
                                            } : undefined}
                                            onOpenEditedMockupInEditor={() => {
                                                const edited = editedMockupsMap[i];
                                                if (edited?.file) {
                                                    // 1. Try to find the live file in the project
                                                    const latestFile = files.find(f => f.name === edited.file.name); // Match by NAME first, as ID might be 'history'

                                                    // 2. Prepare the file to show
                                                    let fileToShow = null;

                                                    if (latestFile) {
                                                        // Found a live version - prefer this!
                                                        fileToShow = latestFile;
                                                        if (edited.file.id === 'history') {
                                                            toast.info(`Opening latest version of ${edited.file.name}`);
                                                        }
                                                    } else if (edited.modifiedContent) {
                                                        // No live file, but we have the edit content (rare for history items, but possible in session)
                                                        fileToShow = {
                                                            ...edited.file,
                                                            content: edited.modifiedContent
                                                        };
                                                    }

                                                    // 3. Open or Show Error
                                                    if (fileToShow && fileToShow.content) {
                                                        setMockupPanelFile(fileToShow);
                                                        setIsMockupDiffMode(false);
                                                        setActivePanel('mockup');
                                                        setShowSidebar(false);
                                                    } else {
                                                        toast.error("Preview unavailable", {
                                                            description: "The content for this older version is not available."
                                                        });
                                                    }
                                                }
                                            }}
                                        />
                                    )
                                })}

                                {/* What the user watches while the model works. Shows
                                    only facts we actually hold: elapsed time, the
                                    model in use, and the context sent this turn. */}
                                <AnimatePresence initial={false}>
                                    {/* Only while we are still waiting on the model.
                                        Once the reply lands it announces its tools,
                                        and the per-message skeletons take over —
                                        showing both would double every card. */}
                                    {loading && !announcedTools && (
                                        <AgentWorkingState
                                            key="working"
                                            startedAt={turnStartedAt}
                                            modelLabel={activeTurnContext?.modelLabel}
                                            contextFiles={activeTurnContext?.files || []}
                                            contextTasks={activeTurnContext?.tasks || []}
                                            fullContext={activeTurnContext?.fullContext}
                                            webSearch={activeTurnContext?.webSearch}
                                            imageCount={activeTurnContext?.imageCount || 0}
                                            reduceMotion={reduceMotion}
                                        />
                                    )}
                                </AnimatePresence>

                                {/* Closes the demo out, so the last thing read
                                    is "that was an example", not an unlabelled
                                    reply that could pass for real history. */}
                                {readOnly && showcasePlayback.phase === 'done' ? (
                                    <ShowcaseOutroCard
                                        reduceMotion={reduceMotion}
                                        onReplay={showcasePlayback.replay}
                                        onGetStarted={() => { window.location.href = '/register'; }}
                                    />
                                ) : null}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>


                    {/* Input Area.
                        A shared-link visitor gets no composer at all. The intro
                        and outro cards already make the pitch; a third one
                        bolted permanently under the transcript was noise, and it
                        competed with the demo it was meant to sell. Only the
                        jump-to-latest control survives — the demo still needs a
                        way back down if you scroll away from it. */}
                    {readOnly ? (
                        <div
                            ref={inputAreaRef}
                            className={cn(
                                'relative pt-2',
                                isMobile ? 'fixed bottom-0 left-0 right-0 z-30 pb-24' : 'pb-6'
                            )}
                        >
                            <div className={cn('relative', columnClass)}>
                                <ScrollToLatest
                                    visible={showJumpToLatest && showTranscript}
                                    pulsing={loading}
                                    reduceMotion={reduceMotion}
                                    onClick={jumpToLatest}
                                />
                            </div>
                        </div>
                    ) : (
                    <div
                        ref={inputAreaRef}
                        // Vertical rhythm and mobile docking only. Horizontal
                        // measure is delegated to `columnClass` below so the
                        // composer cannot drift away from the transcript.
                        className={cn(
                            'relative pt-2',
                            isMobile ? 'fixed bottom-0 left-0 right-0 z-30 bg-background/80 pb-24 backdrop-blur-md' : 'pb-6'
                        )}
                    >
                        <div className={cn('relative', columnClass)}>
                            <ScrollToLatest
                                visible={showJumpToLatest && showTranscript}
                                pulsing={loading}
                                reduceMotion={reduceMotion}
                                onClick={jumpToLatest}
                            />
                            <div className={`relative rounded-2xl bg-secondary/50 border border-white/10 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 transition-all ${isMobile ? 'shadow-2xl' : ''}`}>

                            {/* Reference Picker Popover */}
                            {showMentionPicker && (
                                <div className="absolute bottom-full mb-2 w-72 bg-background/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-64 flex flex-col z-50">
                                    <div className="flex items-center justify-between p-2 border-b border-white/5">
                                        <p className="text-xs text-muted-foreground px-2">Reference a file or task</p>
                                        <span className="text-[10px] font-mono text-muted-foreground/60 px-2">↑↓ · Enter</span>
                                    </div>
                                    <div className="overflow-y-auto flex-1">
                                        {mentionCandidates.map((item, idx) => {
                                            const isSelected = idx === mentionSelectedIndex;
                                            const isTask = item.type === 'task';
                                            return (
                                                <button
                                                    key={`${item.type}-${item.id}`}
                                                    type="button"
                                                    onMouseEnter={() => setMentionSelectedIndex(idx)}
                                                    onClick={() => selectItem(item)}
                                                    className={cn(
                                                        "w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors",
                                                        isSelected ? "bg-primary/15 text-foreground" : "hover:bg-primary/10"
                                                    )}
                                                >
                                                    {isTask ? (
                                                        <SquareCheck className="h-4 w-4 text-green-400 flex-shrink-0" />
                                                    ) : (
                                                        <FileIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                                    )}
                                                    <span className="truncate">{isTask ? item.title : item.name}</span>
                                                    <span className="text-[10px] text-muted-foreground ml-auto">
                                                        {isTask ? 'Task' : 'File'}
                                                    </span>
                                                </button>
                                            );
                                        })}

                                        {mentionCandidates.length === 0 && (
                                            <div className="p-4 text-xs text-muted-foreground text-center">No items found</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Toolbar - Hidden in read-only mode */}
                            {
                                !readOnly && (
                                    <div className="flex items-center justify-between px-4 py-2.5 bg-black/70 backdrop-blur-xl border border-white/10 border-b-0 rounded-t-2xl">
                                        <div className="flex items-center gap-3">
                                            {/* Model Selector */}
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowModelPicker(!showModelPicker)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl bg-secondary/50 hover:bg-secondary/80 transition-colors text-xs ${getModelColor(modelPreset)}`}
                                                >
                                                    {getModelIcon(modelPreset)}
                                                    <span>{models[modelPreset]?.name || 'Fast'}</span>
                                                    <ChevronDown className="h-3 w-3 opacity-50" />
                                                </button>
                                                {showModelPicker && (
                                                    <div className="absolute bottom-full mb-2 left-0 w-56 bg-background/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
                                                        {Object.entries(models).map(([key, model]) => (
                                                            <button
                                                                key={key}
                                                                type="button"
                                                                onClick={() => { setModelPreset(key); setShowModelPicker(false); }}
                                                                className={`w-full text-left px-4 py-3 hover:bg-primary/10 flex items-center gap-3 transition-colors ${modelPreset === key ? 'bg-primary/20' : ''}`}
                                                            >
                                                                <div className={`h-8 w-8 rounded-xl flex items-center justify-center ${getModelColor(key)} bg-secondary/50`}>
                                                                    {getModelIcon(key)}
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium">{model.name}</div>
                                                                    <div className="text-xs text-muted-foreground">{model.description}</div>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="h-5 w-px bg-white/10" />

                                            {/* Toggles */}
                                            <div className="hidden md:flex items-center gap-2">
                                                <Switch id="web-search" checked={isWebSearch} onCheckedChange={setIsWebSearch} className="scale-90" />
                                                <Label htmlFor="web-search" className="text-xs text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                                                    <Globe className="h-3.5 w-3.5" /> Web
                                                </Label>
                                            </div>

                                            <div className="hidden md:flex items-center gap-2">
                                                <Switch id="full-context" checked={isFullContext} onCheckedChange={setIsFullContext} className="scale-90" />
                                                <Label htmlFor="full-context" className="text-xs text-muted-foreground flex items-center gap-1.5 cursor-pointer">
                                                    <Paperclip className="h-3.5 w-3.5" /> All Files
                                                </Label>
                                            </div>

                                            {/* Stated before the send fails, not after. */}
                                            {!isOnline && (
                                                <span
                                                    data-testid="chat-offline-chip"
                                                    className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-red-300"
                                                >
                                                    <WifiOff className="h-3 w-3" />
                                                    Offline
                                                </span>
                                            )}
                                        </div>

                                        {/* Active Conversation Context (Mentions & Retained Session Context) */}
                                        {!isFullContext && activeContextItems.length > 0 && (
                                            <div
                                                className="flex items-center gap-1.5 overflow-x-auto max-w-xl no-scrollbar"
                                                title="Files & tasks active in this conversation's context"
                                            >
                                                {activeContextItems.map((item, idx) => (
                                                    <span
                                                        key={`ctx-${item.type}-${item.label}-${idx}`}
                                                        className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 flex-shrink-0 ${
                                                            item.type === 'task'
                                                                ? 'bg-green-500/10 text-green-400'
                                                                : 'bg-blue-500/10 text-blue-400'
                                                        }`}
                                                    >
                                                        {item.type === 'task' ? (
                                                            <SquareCheck className="h-3 w-3" />
                                                        ) : (
                                                            <FileText className="h-3 w-3" />
                                                        )}
                                                        <span className="truncate max-w-[110px]">{item.label}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            }

                            {/* Input Box - Contains file chips + input + buttons */}
                            {
                                (
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handleSend(e);
                                        }}
                                        className="relative bg-secondary/80 backdrop-blur-xl shadow-2xl rounded-b-2xl border-t-0 border-white/10"
                                    >
                                        {/* Uploaded Files Preview - Inside form for visual cohesion */}
                                        {uploadedFiles.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 px-4 pt-3 pb-2 border-b border-white/5">
                                                {uploadedFiles.map(file => (
                                                    <div
                                                        key={file.id}
                                                        className="group flex items-center gap-1.5 px-2 py-1 bg-primary/15 hover:bg-primary/25 border border-primary/30 rounded-lg text-xs transition-all"
                                                    >
                                                        {/* Image thumbnail or file icon */}
                                                        {file.type === 'image' ? (
                                                            <img
                                                                src={file.dataUrl}
                                                                alt={file.name}
                                                                className="h-6 w-6 rounded object-cover shrink-0"
                                                            />
                                                        ) : (
                                                            <Upload className="h-3 w-3 text-primary shrink-0" />
                                                        )}
                                                        <span className="truncate max-w-[100px] text-primary font-medium">{file.name}</span>
                                                        <span className="text-muted-foreground text-[10px]">
                                                            {file.size > 1024 * 1024
                                                                ? `${(file.size / (1024 * 1024)).toFixed(1)}MB`
                                                                : `${(file.size / 1024).toFixed(0)}KB`
                                                            }
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeUploadedFile(file.id)}
                                                            className="ml-0.5 p-0.5 rounded hover:bg-white/20 text-primary/60 hover:text-white transition-colors"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Backdrop for closing tooltips */}
                                        {(showModelPicker || showMentionPicker) && (
                                            <div
                                                className="fixed inset-0 z-30"
                                                onClick={() => {
                                                    setShowModelPicker(false);
                                                    setShowMentionPicker(false);
                                                }}
                                            />
                                        )}

                                        {/* Input Row */}
                                        <div className="flex items-end min-h-[56px]">
                                            <div className="flex-1 relative min-h-[56px]">
                                                <RichInput
                                                    ref={inputRef}
                                                    value={input}
                                                    onChange={handleInputChange}
                                                    onPaste={handlePaste}
                                                    onChipDelete={(tagContent) => {
                                                        // Parse the tag to get the name, e.g. "@[File: filename.md]" -> "filename.md"
                                                        const fileMatch = tagContent.match(/@\[File: ([^\]]+)\]/);
                                                        const taskMatch = tagContent.match(/@\[Task: ([^\]]+)\]/);

                                                        if (fileMatch) {
                                                            const fileName = fileMatch[1];
                                                            setReferencedItems(prev => prev.filter(item =>
                                                                !(item.type === 'file' && item.name === fileName)
                                                            ));
                                                        } else if (taskMatch) {
                                                            const taskTitle = taskMatch[1];
                                                            setReferencedItems(prev => prev.filter(item =>
                                                                !(item.type === 'task' && item.title === taskTitle)
                                                            ));
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (showMentionPicker && mentionCandidates.length > 0) {
                                                            if (e.key === 'ArrowDown') {
                                                                e.preventDefault();
                                                                setMentionSelectedIndex(i => (i + 1) % mentionCandidates.length);
                                                                return;
                                                            }
                                                            if (e.key === 'ArrowUp') {
                                                                e.preventDefault();
                                                                setMentionSelectedIndex(i => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
                                                                return;
                                                            }
                                                            if (e.key === 'Enter' || e.key === 'Tab') {
                                                                e.preventDefault();
                                                                if (e.nativeEvent?.isComposing || e.isComposing || e.keyCode === 229) return;
                                                                selectItem(mentionCandidates[mentionSelectedIndex] || mentionCandidates[0]);
                                                                return;
                                                            }
                                                            if (e.key === 'Escape') {
                                                                e.preventDefault();
                                                                setShowMentionPicker(false);
                                                                return;
                                                            }
                                                        }
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            if (e.nativeEvent?.isComposing || e.isComposing || e.keyCode === 229) return;
                                                            handleSend(e);
                                                        }
                                                    }}
                                                    onFocus={() => setInputFocused(true)}
                                                    onBlur={() => setInputFocused(false)}
                                                    // Empty while the overlay is up: the two would
                                                    // otherwise render on top of each other.
                                                    placeholder={showTypewriter ? '' : 'Ask anything... (@ to reference files/tasks)'}
                                                    className="w-full min-h-[56px] max-h-[200px] overflow-y-auto rounded-bl-2xl bg-transparent"
                                                />
                                                <ChatPlaceholderOverlay
                                                    active={showTypewriter}
                                                    examples={placeholderExamples}
                                                    staticFallback="Ask anything... (@ to reference files/tasks)"
                                                />
                                            </div>
                                            <div className="h-[56px] flex items-center gap-1 pr-2">
                                                {/* Hidden File Input */}
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    multiple
                                                    accept=".md,.txt,.json,.js,.jsx,.ts,.tsx,.py,.java,.c,.cpp,.go,.rs,.yaml,.yml,.xml,.html,.css,.scss,.png,.jpg,.jpeg,.gif,.webp,image/*"
                                                    className="hidden"
                                                    onChange={handleFileInputChange}
                                                />
                                                {/* Upload Button */}
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="h-10 w-10 rounded-xl hover:bg-primary/10 transition-all shrink-0 text-muted-foreground hover:text-primary"
                                                    title="Upload files"
                                                >
                                                    <Paperclip className="h-5 w-5" />
                                                </Button>
                                                {/* Send / Stop — one control, two states. Being
                                                    able to leave the wait is the single biggest
                                                    reduction in how long it feels. */}
                                                <Button
                                                    type={loading ? 'button' : 'submit'}
                                                    onClick={loading ? handleStopGeneration : undefined}
                                                    size="icon"
                                                    data-testid={loading ? 'chat-stop' : 'chat-send'}
                                                    title={loading ? 'Stop generating (Esc)' : isOnline ? 'Send' : 'Offline — reconnect to send'}
                                                    disabled={
                                                        !loading && (
                                                            !isOnline ||
                                                            (!input.trim() && attachedSelections.length === 0 && uploadedFiles.length === 0)
                                                        )
                                                    }
                                                    className={cn(
                                                        'h-10 w-10 shrink-0 overflow-hidden rounded-xl transition-colors duration-200',
                                                        loading
                                                            ? 'border border-white/15 bg-white/10 text-red-400 hover:border-red-500/40 hover:bg-red-500/20'
                                                            : 'bg-primary hover:bg-primary/90 disabled:opacity-50'
                                                    )}
                                                >
                                                    <AnimatePresence mode="wait" initial={false}>
                                                        <motion.span
                                                            key={loading ? 'stop' : 'send'}
                                                            initial={reduceMotion ? false : { scale: 0.55, opacity: 0, rotate: -60 }}
                                                            animate={{ scale: 1, opacity: 1, rotate: 0 }}
                                                            exit={reduceMotion ? { opacity: 0 } : { scale: 0.55, opacity: 0, rotate: 60 }}
                                                            transition={{ duration: 0.14, ease: 'easeOut' }}
                                                            className="flex items-center justify-center"
                                                        >
                                                            {loading ? <Square className="h-3.5 w-3.5 fill-current" /> : <Send className="h-5 w-5" />}
                                                        </motion.span>
                                                    </AnimatePresence>
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                )
                            }
                            </div >
                        </div >
                    </div >
                    )}
                </div >

                {/* Delete Confirmation Modal */}
                < Dialog open={!!deleteConfirmSession} onOpenChange={(open) => !open && setDeleteConfirmSession(null)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Delete Conversation?</DialogTitle>
                            <DialogDescription>
                                This action cannot be undone. This will permanently delete this conversation and all its messages.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button
                                variant="ghost"
                                onClick={() => setDeleteConfirmSession(null)}
                                className="rounded-xl"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={confirmDeleteSession}
                                className="rounded-xl"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog >
            </div >
        </Surface>

            {/* ═══════════════════════════════════════════════════════════════
                ARTIFACT SURFACES

                Peers of the conversation surface, not children of it: they are
                direct flex items of the Layout's row, which owns the gap between
                them. Only one can be open at a time — that is now structural,
                enforced by `activePanel` holding a single id — but they share one
                AnimatePresence so a swap cross-fades rather than blinking
                through an empty gap.

                Each carries the same switcher strip, so a turn that produced
                both a document and a changeset leaves both reachable.
            ═══════════════════════════════════════════════════════════════ */}
            <AnimatePresence>
                {editorPanelOpen && agentFile && (
                    <ArtifactSurface
                        key="editor"
                        label={agentFile.name}
                        resize={{ min: 420, max: 900, initial: 520, storageKey: 'forge:panel:editor' }}
                        toolbar={panelSwitcher}
                    >
                        <AgentEditorPanel
                            file={agentFile}
                            originalContent={originalContent}
                            isDiffMode={isDiffMode}
                            editSummary={editSummary}
                            onContentChange={handleAgentFileContentChange}
                            onAcceptChanges={handleAcceptChanges}
                            onRejectChanges={handleRejectChanges}
                            onOpenInEditor={handleOpenInFullEditor}
                            onClose={handleCloseEditorPanel}
                            isSaving={isSavingFile}
                            onAddToChat={handleAddToChat}
                            isAccepted={isDiffAccepted}
                            readOnly={readOnly}
                            onOpenLatest={() => {
                                const latest = files.find(f => f.name === agentFile.name);
                                if (latest) {
                                    setAgentFile(latest);
                                    setIsDiffMode(false);
                                    toast.info(`Switched to latest version of ${latest.name}`);
                                } else {
                                    toast.error("Latest version NOT found in project");
                                }
                            }}
                        />
                    </ArtifactSurface>
                )}

                {/* Mockup Preview Panel - For UI mockups */}
                {mockupPanelOpen && mockupPanelFile && (
                    <ArtifactSurface
                        key="mockup"
                        label={mockupPanelFile.name}
                        resize={{ min: 350, max: 900, initial: 500, storageKey: 'forge:panel:mockup' }}
                        toolbar={panelSwitcher}
                    >
                        <MockupPreviewPanel
                            file={mockupPanelFile}
                            originalContent={mockupOriginalContent}
                            isDiffMode={isMockupDiffMode}
                            editSummary={mockupEditSummary}
                            onContentChange={handleMockupContentChange}
                            onAcceptChanges={handleAcceptMockupChanges}
                            onRejectChanges={handleRejectMockupChanges}
                            onOpenInEditor={handleOpenMockupInFullEditor}
                            onClose={handleCloseMockupPanel}
                            isSaving={false}
                            isAccepted={isMockupDiffAccepted}
                            readOnly={readOnly}
                            onOpenLatest={() => {
                                const latest = files.find(f => f.name === mockupPanelFile.name);
                                if (latest) {
                                    setMockupPanelFile(latest);
                                    setIsMockupDiffMode(false);
                                    toast.info(`Switched to latest version of ${latest.name}`);
                                } else {
                                    toast.error("Latest version NOT found in project");
                                }
                            }}
                        />
                    </ArtifactSurface>
                )}

                {/* Task Agent Panel - agent changeset + live editable board */}
                {taskPanelOpen && (
                    <ArtifactSurface
                        key="tasks"
                        label="Tasks"
                        resize={{ min: TASK_PANEL_MIN_WIDTH, max: TASK_PANEL_MAX_WIDTH, initial: 440, storageKey: 'forge:panel:tasks' }}
                        toolbar={panelSwitcher}
                    >
                        <TaskAgentPanel
                            tab={taskPanelTab}
                            onTabChange={setTaskPanelTab}
                            batches={panelBatches}
                            tasks={tasks}
                            readOnly={readOnly}
                            isLoading={taskChangesetsQuery.isLoading}
                            focusBatchId={focusBatchId}
                            projectBaseUrl={baseUrl}
                            revertingIds={revertingIds}
                            onClose={handleCloseTaskPanel}
                            onCreateTask={handlePanelCreateTask}
                            onUpdateTask={handlePanelUpdateTask}
                            onDeleteTask={handlePanelDeleteTask}
                            onRevertChange={handleRevertChange}
                            onRevertBatch={handleRevertBatch}
                        />
                    </ArtifactSurface>
                )}
            </AnimatePresence>
        </>
    );
}
